import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { FaExclamationTriangle, FaArrowLeft, FaChevronUp, FaCheckCircle, FaChevronDown, FaEdit, FaTrash, FaEye, FaEyeSlash, FaCommentDots, FaTimes, FaCopy, FaPaste, FaUndo, FaPlus, FaUpload, FaFileExcel, FaSpinner } from 'react-icons/fa';
import axiosInstance from '../api/axios';
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { ConfirmModal, NoticeModal, PromptModal } from '../Dialogs';

// --- Helper to format interface names gracefully ---
const getInterfaceLabel = (key, flatInterfaces) => {
    const found = flatInterfaces.find(i => i.key === key);
    if (found) return found.label;
    return (key || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

// --- Helper to check for missing values in custom fields ---
const isCustomFieldIncomplete = (field) => {
    return field.is_custom && (
        !field.file_header_name?.trim() ||
        !field.data_type?.trim() ||
        !field.table_in_relex?.trim() ||
        !field.ui_field_in_relex?.trim() ||
        !field.used_for?.trim() ||
        !field.description?.trim() ||
        !field.example_value?.trim()
    );
};

// --- Field Selector Modal ---
const FieldSelectorModal = ({
    isOpen,
    onClose,
    interfaceName,
    fields,
    selectedFieldIds,
    omissionReasons,
    mandatoryOverrides,
    onSelectionChange,
    onReasonChange,
    onMandatoryOverrideChange,
    onFieldAdded,
    onFieldUpdated,
    onFieldDeleted,
    flatInterfaces
}) => {
    const { projectId } = useParams();
    const [formMode, setFormMode] = useState(null);
    const [fieldData, setFieldData] = useState({
        db_field_name: '', file_header_name: '', data_type: 'STRING',
        description: '', is_mandatory: false, is_custom: true, table_in_relex: '',
        ui_field_in_relex: '', used_for: '', example_value: '', source_file: ''
    });
    const [editingFieldId, setEditingFieldId] = useState(null);
    const [error, setError] = useState('');
    const [availableSourceFiles, setAvailableSourceFiles] = useState([]);
    const [notice, setNotice] = useState(null);
    const [overridingField, setOverridingField] = useState(null);
    const [deletingField, setDeletingField] = useState(null);

    // --- CSV Import States & Refs ---
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);
    const modalBodyRef = useRef(null);

    const allFieldsFlat = useMemo(() => {
        return Object.values(fields).flat();
    }, [fields]);

    useEffect(() => {
        if (isOpen && interfaceName) {
            const fetchSourceFiles = async () => {
                try {
                    const response = await axiosInstance.get(
                        `/api/master-interfaces/${interfaceName}/source-files`,
                        { params: { project_id: projectId || undefined } }
                    );
                    setAvailableSourceFiles(response.data || []);
                } catch (err) {
                    if (err.response?.status === 401) return;
                    console.error("Failed to fetch source files:", err);
                    setAvailableSourceFiles([interfaceName]);
                }
            };
            fetchSourceFiles();
        }
        if (!isOpen) {
            setFormMode(null);
            setError('');
        }
    }, [isOpen, interfaceName, projectId]);

    if (!isOpen) return null;

    const handleImportCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsImporting(true);
        setError('');

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                if (!text.trim()) throw new Error("File is empty");

                const firstNewline = text.indexOf('\n');
                const firstLine = firstNewline > -1 ? text.substring(0, firstNewline) : text;
                const delimiter = firstLine.includes(';') ? ';' : ',';

                const parseCSVText = (rawText) => {
                    const parseLine = (str) => {
                        const rows = [];
                        let currentRow = [];
                        let curVal = '';
                        let inQuotes = false;

                        for (let i = 0; i < str.length; i++) {
                            const char = str[i];
                            if (inQuotes) {
                                if (char === '"') {
                                    if (i + 1 < str.length && str[i + 1] === '"') {
                                        curVal += '"'; i++;
                                    } else {
                                        inQuotes = false;
                                    }
                                } else {
                                    curVal += char;
                                }
                            } else {
                                if (char === '"') {
                                    inQuotes = true;
                                } else if (char === delimiter) {
                                    currentRow.push(curVal.trim());
                                    curVal = '';
                                } else if (char === '\n' || char === '\r') {
                                    currentRow.push(curVal.trim());
                                    if (currentRow.some(c => c !== '')) rows.push(currentRow);
                                    currentRow = [];
                                    curVal = '';
                                    if (char === '\r' && i + 1 < str.length && str[i + 1] === '\n') i++;
                                } else {
                                    curVal += char;
                                }
                            }
                        }
                        currentRow.push(curVal.trim());
                        if (currentRow.some(c => c !== '')) rows.push(currentRow);
                        return rows;
                    };

                    const initialRows = parseLine(rawText);
                    const finalRows = [];

                    for (const row of initialRows) {
                        if (row.length === 1 && row[0].includes(delimiter)) {
                            const unwrappedRows = parseLine(row[0]);
                            finalRows.push(...unwrappedRows);
                        } else {
                            finalRows.push(row);
                        }
                    }
                    return finalRows;
                };

                const parsedRows = parseCSVText(text);

                let headerRowIndex = -1;
                let headers = [];

                for (let i = 0; i < parsedRows.length; i++) {
                    const row = parsedRows[i];
                    if (row.some(col => col.toLowerCase().includes('db field in relex') || col.toLowerCase().includes('header name in file'))) {
                        headerRowIndex = i;
                        headers = row.map(h => h.toLowerCase());
                        break;
                    }
                }

                if (headerRowIndex === -1) {
                    throw new Error("Could not find the 'DB Field in RELEX' header row in this file.");
                }

                const colIdx = {
                    name: headers.findIndex(h => h.includes('db field in relex') || h.includes('header name in file')),
                    table: headers.findIndex(h => h.includes('table in relex')),
                    uiField: headers.findIndex(h => h.includes('ui field in relex')),
                    desc: headers.findIndex(h => h === 'description'),
                    usedFor: headers.findIndex(h => h === 'used for'),
                    mandatory: headers.findIndex(h => h === 'must have' || h === 'mandatory'),
                    format: headers.findIndex(h => h === 'format' || h === 'data type'),
                    example: headers.findIndex(h => h === 'example')
                };

                const newSelectedIds = new Set(selectedFieldIds);
                const newlyCreatedFields = [];
                const createdHeadersInThisRun = new Set();

                for (let i = headerRowIndex + 1; i < parsedRows.length; i++) {
                    const row = parsedRows[i];

                    if (row.length < 3) continue;

                    const rawFieldName = colIdx.name > -1 && row[colIdx.name] ? row[colIdx.name] : '';
                    if (!rawFieldName) continue;

                    if (rawFieldName.length > 60 || rawFieldName.toLowerCase().includes('e.g.') || rawFieldName.toLowerCase().includes('i.e.')) {
                        continue;
                    }

                    const cleanHeaderName = rawFieldName
                        .toLowerCase()
                        .replace(/[^a-z0-9_]/g, '_')
                        .replace(/_+/g, '_')
                        .replace(/^_+|_+$/g, '');

                    if (!cleanHeaderName || cleanHeaderName.length > 60 || createdHeadersInThisRun.has(cleanHeaderName)) continue;

                    const existingField = allFieldsFlat.find(f =>
                        (f.file_header_name && f.file_header_name.toLowerCase() === cleanHeaderName) ||
                        (f.db_field_name && f.db_field_name.toLowerCase() === cleanHeaderName) ||
                        (f.file_header_name && f.file_header_name.toLowerCase() === `custom_${cleanHeaderName}`) ||
                        (f.db_field_name && f.db_field_name.toLowerCase() === `custom_${cleanHeaderName}`)
                    );

                    if (existingField) {
                        newSelectedIds.add(existingField.id);
                    } else {
                        const formattedHeader = cleanHeaderName.startsWith('custom_') ? cleanHeaderName : `custom_${cleanHeaderName}`;

                        let dataType = 'STRING';
                        if (colIdx.format > -1 && row[colIdx.format]) {
                            const rawFormat = row[colIdx.format].toUpperCase();
                            if (rawFormat.includes('DATE')) dataType = 'DATE';
                            else if (rawFormat.includes('INT')) dataType = 'INTEGER';
                            else if (rawFormat.includes('DOUBLE') || rawFormat.includes('FLOAT') || rawFormat.includes('DECIMAL')) dataType = 'DOUBLE';
                            else if (rawFormat.includes('BOOL')) dataType = 'BOOLEAN';
                        }

                        const isMandatory = colIdx.mandatory > -1 && ['x', 'yes', 'true', '1'].includes((row[colIdx.mandatory] || '').trim().toLowerCase());

                        let description = colIdx.desc > -1 && row[colIdx.desc] ? row[colIdx.desc] : '';
                        if (!description && colIdx.usedFor > -1) description = row[colIdx.usedFor];
                        if (!description) description = 'Automatically imported from sample CSV';

                        const newFieldData = {
                            db_field_name: formattedHeader,
                            file_header_name: formattedHeader,
                            data_type: dataType,
                            description: description,
                            is_mandatory: isMandatory,
                            is_custom: true,
                            table_in_relex: colIdx.table > -1 ? row[colIdx.table] : '',
                            ui_field_in_relex: colIdx.uiField > -1 ? row[colIdx.uiField] : '',
                            used_for: colIdx.usedFor > -1 ? row[colIdx.usedFor] : '',
                            example_value: colIdx.example > -1 ? row[colIdx.example] : '',
                            source_file: availableSourceFiles.length > 0 ? availableSourceFiles[0] : ''
                        };

                        try {
                            const response = await axiosInstance.post(`/api/projects/${projectId}/interfaces/${interfaceName}/fields`, newFieldData);
                            newlyCreatedFields.push(response.data);
                            newSelectedIds.add(response.data.id);
                            createdHeadersInThisRun.add(cleanHeaderName);
                        } catch (err) {
                            console.error(`Failed to auto-create field ${formattedHeader}:`, err);
                        }
                    }
                }

                if (newlyCreatedFields.length > 0) {
                    await onFieldAdded(newlyCreatedFields, interfaceName);
                }

                onSelectionChange(interfaceName, Array.from(newSelectedIds));
                setNotice({
                    tone: 'success',
                    title: 'Import Complete',
                    message: `Created ${newlyCreatedFields.length} new custom field(s) and marked existing fields as selected.`
                });

            } catch (err) {
                setError(err.message || "Failed to parse CSV file");
            } finally {
                setIsImporting(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        };
        reader.readAsText(file);
    };

    const handleOverrideMandatory = (field) => {
        setOverridingField(field);
    };

    const confirmOverrideMandatory = (reason) => {
        const field = overridingField;
        setOverridingField(null);
        if (!field) return;

        onMandatoryOverrideChange(interfaceName, field.id, false);
        onReasonChange(interfaceName, field.id, reason);
        if (!selectedFieldIds.includes(field.id)) {
            onSelectionChange(interfaceName, [...selectedFieldIds, field.id]);
        }
    };

    const handleRestoreMandatory = (field) => {
        onMandatoryOverrideChange(interfaceName, field.id, null);
        onReasonChange(interfaceName, field.id, null);
        if (!selectedFieldIds.includes(field.id)) {
            onSelectionChange(interfaceName, [...selectedFieldIds, field.id]);
        }
    };

    const handleCheckboxChange = (field, isChecked) => {
        const isOriginallyMandatory = field.is_mandatory;
        const isOverriddenToOptional = mandatoryOverrides[field.id] === false;

        if (isOriginallyMandatory && !isOverriddenToOptional) {
            return;
        }

        const newSelectedIds = isChecked
            ? [...selectedFieldIds, field.id]
            : selectedFieldIds.filter(id => id !== field.id);
        onSelectionChange(interfaceName, newSelectedIds);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'combined_field_name') {
            setFieldData(prev => ({ ...prev, db_field_name: value, file_header_name: value }));
        } else {
            setFieldData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const openAddFieldForm = () => {
        setFormMode('add');
        setEditingFieldId(null);
        setFieldData({
            db_field_name: '', file_header_name: '', data_type: 'STRING',
            description: '', is_mandatory: false, is_custom: true, table_in_relex: '',
            ui_field_in_relex: '', used_for: '', example_value: '',
            source_file: availableSourceFiles.length > 0 ? availableSourceFiles[0] : ''
        });
        if (modalBodyRef.current) {
            modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const openEditFieldForm = (fieldToEdit) => {
        setFormMode('edit');
        setEditingFieldId(fieldToEdit.id);

        setFieldData({
            db_field_name: fieldToEdit.db_field_name ?? '',
            file_header_name: fieldToEdit.file_header_name ?? '',
            data_type: fieldToEdit.data_type ?? 'STRING',
            description: fieldToEdit.description ?? '',
            is_mandatory: fieldToEdit.is_mandatory ?? false,
            is_custom: fieldToEdit.is_custom ?? true,
            table_in_relex: fieldToEdit.table_in_relex ?? '',
            ui_field_in_relex: fieldToEdit.ui_field_in_relex ?? '',
            used_for: fieldToEdit.used_for ?? '',
            example_value: fieldToEdit.example_value ?? '',
            source_file: fieldToEdit.source_file ?? (availableSourceFiles.length > 0 ? availableSourceFiles[0] : '')
        });
        if (modalBodyRef.current) {
            modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!fieldData.db_field_name.trim()) {
            setError('DB Field / File Header Name is required.');
            return;
        }

        if (!fieldData.source_file) {
            setError('Source File selection is required.');
            return;
        }

        try {
            if (formMode === 'add') {
                const response = await axiosInstance.post(`/api/projects/${projectId}/interfaces/${interfaceName}/fields`, fieldData);
                onFieldAdded(response.data, interfaceName);
            } else if (formMode === 'edit' && editingFieldId) {
                const response = await axiosInstance.put(`/api/master-interface-fields/${editingFieldId}`, fieldData);
                onFieldUpdated(response.data, interfaceName);
            }
            setFormMode(null);
        } catch (err) {
            if (err.response?.status === 401) return;
            setError(err.response?.data?.detail || err.message);
        }
    };

    const handleDeleteField = (fieldToDelete) => {
        if (!fieldToDelete.is_custom) {
            setNotice({ tone: 'error', title: 'Cannot Delete Field', message: 'Standard fields cannot be deleted.' });
            return;
        }
        setDeletingField(fieldToDelete);
    };

    const confirmDeleteField = async () => {
        const fieldToDelete = deletingField;
        setDeletingField(null);
        if (!fieldToDelete) return;

        try {
            await axiosInstance.delete(`/api/master-interface-fields/${fieldToDelete.id}`);
            onFieldDeleted(fieldToDelete.id, interfaceName);
        } catch (err) {
            if (err.response?.status === 401) return;
            setNotice({
                tone: 'error',
                title: 'Could Not Delete Field',
                message: err.response?.data?.detail || 'Failed to delete field.'
            });
        }
    };

    const handleSelectAll = () => {
        const allFieldIds = allFieldsFlat.map(f => f.id);
        onSelectionChange(interfaceName, allFieldIds);
        allFieldsFlat.forEach(field => {
            if (field.is_mandatory && mandatoryOverrides[field.id] === false) {
                onMandatoryOverrideChange(interfaceName, field.id, null);
                onReasonChange(interfaceName, field.id, null);
            }
        });
    };

    const handleDeselectAll = () => {
        const mandatorySelectedIds = [];
        allFieldsFlat.forEach(field => {
            if (field.is_mandatory && mandatoryOverrides[field.id] !== false) {
                mandatorySelectedIds.push(field.id);
            }
        });
        onSelectionChange(interfaceName, mandatorySelectedIds);
    };

    return (
        <>
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Fields for {getInterfaceLabel(interfaceName, flatInterfaces || [])}</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <div className="modal-body" ref={modalBodyRef}>
                    <div className="dropdown-actions">
                        <span>{selectedFieldIds.length} / {allFieldsFlat.length} selected</span>
                        <div>
                            <button type="button" onClick={handleSelectAll}>Select All</button>
                            <button type="button" onClick={handleDeselectAll}>Deselect Optional</button>

                            <input
                                type="file"
                                accept=".csv"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onChange={handleImportCSV}
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current.click()}
                                disabled={isImporting}
                                className="import-csv-button"
                            >
                                {isImporting ? <span className="spinner-small" /> : <FaUpload style={{ marginRight: '5px' }} />}
                                {isImporting ? 'Importing...' : 'Auto-Select from CSV'}
                            </button>

                            <button type="button" onClick={formMode ? () => setFormMode(null) : openAddFieldForm} className="add-field-button">
                                {formMode ? 'Cancel' : '+ Add Custom Field'}
                            </button>
                        </div>
                    </div>

                    {formMode && (
                        <form onSubmit={handleFormSubmit} className="add-field-form">
                            <h4>{formMode === 'add' ? 'New Custom Field' : 'Edit Custom Field'}</h4>
                            {error && <p className="form-feedback error small">{error}</p>}
                            <div className="add-field-grid">
                                <input name="combined_field_name" value={fieldData.db_field_name} onChange={handleInputChange} placeholder="DB Field / File Header Name (required)" className="span-two-columns" required />
                                <input name="table_in_relex" value={fieldData.table_in_relex} onChange={handleInputChange} placeholder="Table in RELEX" />
                                <input name="ui_field_in_relex" value={fieldData.ui_field_in_relex} onChange={handleInputChange} placeholder="UI Field in RELEX" />
                                <select name="data_type" value={fieldData.data_type} onChange={handleInputChange}>
                                    <option>STRING</option><option>DATE</option><option>INTEGER</option><option>DOUBLE</option><option>BOOLEAN</option>
                                </select>
                                <select name="source_file" value={fieldData.source_file} onChange={handleInputChange} required>
                                    <option value="" disabled>-- Select Source File --</option>
                                    {availableSourceFiles.map(file => <option key={file} value={file}>{file}</option>)}
                                </select>
                                <textarea name="used_for" value={fieldData.used_for} onChange={handleInputChange} placeholder="Used For" rows="2"></textarea>
                                <textarea name="description" value={fieldData.description} onChange={handleInputChange} placeholder="Description" rows="2"></textarea>
                                <textarea name="example_value" value={fieldData.example_value} onChange={handleInputChange} placeholder="Example Value" rows="2"></textarea>
                            </div>
                            <div className="add-field-footer">
                                <div className="field-form-checkboxes">
                                    <label><input type="checkbox" name="is_mandatory" checked={fieldData.is_mandatory} onChange={handleInputChange} /> Mandatory</label>
                                </div>
                                <button type="submit" className="action-button primary">Save Field</button>
                            </div>
                        </form>
                    )}

                    <div className="field-groups-container">
                        {Object.keys(fields).length === 0 ? (
                            <div className="no-fields-message-container">
                                <p>No fields exist for this interface yet.</p>
                                <p className="sub-text">Click <strong>+ Add Custom Field</strong> above to get started.</p>
                            </div>
                        ) : (
                            Object.entries(fields).sort().map(([fileName, fileFields]) => (
                                <div key={fileName} className="file-group">
                                    <h4 className="file-group-header">{fileName}</h4>
                                    <ul className="field-list">
                                        {fileFields.map(field => {
                                            const isSelected = selectedFieldIds.includes(field.id);
                                            const isOriginallyMandatory = field.is_mandatory;
                                            const isOverriddenToOptional = mandatoryOverrides[field.id] === false;
                                            const effectiveStatus = isOriginallyMandatory && !isOverriddenToOptional ? 'Mandatory' : 'Optional';
                                            const reason = omissionReasons[field.id];

                                            return (
                                                <li key={field.id} className={`field-item ${isOriginallyMandatory ? 'is-originally-mandatory' : ''} ${isOverriddenToOptional ? 'is-overridden-optional' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        id={`field-modal-${field.id}`}
                                                        checked={isSelected}
                                                        onChange={(e) => handleCheckboxChange(field, e.target.checked)}
                                                        disabled={isOriginallyMandatory && !isOverriddenToOptional}
                                                    />
                                                    <div className="field-item-content">
                                                        <label htmlFor={`field-modal-${field.id}`} className="field-item-label-container">
                                                            <span className="field-label-text">{field.file_header_name || field.db_field_name}</span>
                                                            <div className="field-tags-and-actions">
                                                                {effectiveStatus === 'Mandatory' && (
                                                                    <span className="status-tag mandatory interactive-tag">
                                                                        Mandatory
                                                                        <button
                                                                            type="button"
                                                                            className="override-button"
                                                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOverrideMandatory(field); }}
                                                                            title="Override to Optional (requires reason)"
                                                                        >
                                                                            <FaTimes />
                                                                        </button>
                                                                    </span>
                                                                )}
                                                                {effectiveStatus === 'Optional' && isOriginallyMandatory && (
                                                                    <span
                                                                        className="status-tag optional overridden-tag"
                                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRestoreMandatory(field); }}
                                                                        title="Click to restore Mandatory status"
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        Optional (Overridden)
                                                                    </span>
                                                                )}
                                                                {effectiveStatus === 'Optional' && !isOriginallyMandatory && (
                                                                    <span className="status-tag optional">Optional</span>
                                                                )}
                                                                {field.is_custom && (
                                                                <>
                                                                    <span className="custom-tag">Custom</span>
                                                                    
                                                                    {/* --- NEW: Missing Values Warning --- */}
                                                                    {isCustomFieldIncomplete(field) && (
                                                                        <FaExclamationTriangle 
                                                                            style={{ color: '#f59e0b', fontSize: '1.1rem', cursor: 'help', marginLeft: '4px' }} 
                                                                            title="Missing values on custom fields" 
                                                                        />
                                                                    )}

                                                                    <button onClick={(e) => { e.preventDefault(); openEditFieldForm(field); }} className="field-action-btn edit" title="Edit Custom Field"><FaEdit /></button>
                                                                        <button onClick={(e) => { e.preventDefault(); handleDeleteField(field); }} className="field-action-btn delete" title="Delete Custom Field"><FaTrash /></button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </label>
                                                        {isOverriddenToOptional && reason && (
                                                            <div className="omission-reason-display">
                                                                <FaCommentDots className="reason-icon" />
                                                                <p className="reason-text">{reason}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button type="button" onClick={onClose} className="action-button primary">Done</button>
                </div>
            </div>
        </div>

        <PromptModal
            isOpen={!!overridingField}
            title="Override Mandatory Field"
            message={<>
                <strong style={{ color: 'var(--text-color)' }}>{overridingField?.db_field_name}</strong> is mandatory by default. Overriding it makes the field optional for this project only.
            </>}
            label="Reason for overriding *"
            placeholder="e.g., Not tracked in the source system..."
            requiredMessage="A reason is required to override the mandatory status."
            confirmLabel="Override to Optional"
            onConfirm={confirmOverrideMandatory}
            onClose={() => setOverridingField(null)}
        />

        <ConfirmModal
            isOpen={!!deletingField}
            tone="danger"
            title="Delete Custom Field?"
            message={<>
                <strong style={{ color: 'var(--text-color)' }}>{deletingField?.file_header_name || deletingField?.db_field_name}</strong> will be permanently deleted.
                <br /><br />
                This cannot be undone.
            </>}
            confirmLabel="Delete Field"
            cancelLabel="Keep Field"
            onConfirm={confirmDeleteField}
            onClose={() => setDeletingField(null)}
        />

        <NoticeModal
            isOpen={!!notice}
            tone={notice?.tone}
            title={notice?.title}
            message={notice?.message}
            onClose={() => setNotice(null)}
        />
        </>
    );
};

// --- Custom Interface Creation Modal ---
const CreateCustomInterfaceModal = ({ isOpen, onClose, onSubmit, dropName }) => {
    const [interfaceName, setInterfaceName] = useState('');
    const [interfaceType, setInterfaceType] = useState('Transactions');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setInterfaceName('');
            setInterfaceType('Transactions');
            setDescription('');
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const formattedName = interfaceName.trim().toLowerCase().replace(/\s+/g, '_');
        if (!formattedName) { setError("Please provide an interface name."); return; }

        onSubmit({
            interface_name: formattedName,
            interface_drop: dropName,
            interface_type: interfaceType,
            description: description,
            fields: []
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Create Custom Interface</h3>
                    <button onClick={onClose} className="modal-close-button">&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem' }}>
                        <p style={{ fontSize: '0.9rem', color: 'var(--light-text)', margin: 0, paddingBottom: '0.5rem' }}>
                            This interface will be created exclusively for <strong>{dropName}</strong>.
                        </p>
                        {error && <p className="form-feedback error small" style={{ margin: 0 }}>{error}</p>}
                        <div className="input-group">
                            <label className="input-label">Interface Name</label>
                            <input type="text" value={interfaceName} onChange={e => { setInterfaceName(e.target.value); if (error) setError(''); }} required placeholder="e.g., Special Client Data" autoFocus />
                        </div>
                        <div className="input-group">
                            <label className="input-label">Interface Type</label>
                            <select value={interfaceType} onChange={e => setInterfaceType(e.target.value)}>
                                <option value="Transactions">Transactions</option>
                                <option value="Master data">Master data</option>
                            </select>
                        </div>
                        <div className="input-group">
                            <label className="input-label">Description (Optional)</label>
                            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this interface used for?" rows="3" />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="action-button secondary">Cancel</button>
                        <button type="submit" className="action-button primary">Create</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- DYNAMIC METADATA MAP ---
const INTERFACE_METADATA = {
    // Drop 1 - Master Data
    "merchandising_product_groups": { category: "Drop 1 - Master Data", label: "Merchandising product groups", modules: ["S&A"] },
    "locations": { category: "Drop 1 - Master Data", label: "Locations", modules: ["F&R", "S&A", "P&P"] },
    "product_groups": { category: "Drop 1 - Master Data", label: "Product groups", modules: ["F&R", "S&A", "P&P"] },
    "product_locations": { category: "Drop 1 - Master Data", label: "Product-locations", modules: ["F&R", "S&A"] },
    "products": { category: "Drop 1 - Master Data", label: "Products", modules: ["F&R", "S&A", "P&P"] },
    "promotion_assortments": { category: "Drop 1 - Master Data", label: "Promotion assortments", modules: ["P&P"] },
    "campaigns": { category: "Drop 1 - Master Data", label: "Campaigns", modules: ["F&R", "P&P"] },
    "price_calendars": { category: "Drop 1 - Master Data", label: "Price calendars", modules: ["F&R"] },
    "product_location_price_calendars": { category: "Drop 1 - Master Data", label: "Product-location price calendars", modules: ["F&R"] },
    "sales_prices": { category: "Drop 1 - Master Data", label: "Sales prices", modules: ["P&P"] },
    "purchase_prices": { category: "Drop 1 - Master Data", label: "Purchase prices", modules: ["P&P"] },
    "competitor_prices": { category: "Drop 1 - Master Data", label: "Competitor prices", modules: ["P&P"] },
    "product_location_campaigns": { category: "Drop 1 - Master Data", label: "Product-location-campaigns", modules: ["F&R", "P&P"] },
    "location_hierarchy": { category: "Drop 1 - Master Data", label: "Location hierarchy", modules: ["F&R", "S&A"] },
    "opening_times": { category: "Drop 1 - Master Data", label: "Opening times", modules: ["F&R"] },
    "closed_dates": { category: "Drop 1 - Master Data", label: "Closed dates", modules: ["F&R"] },
    "assortments": { category: "Drop 1 - Master Data", label: "Assortments", modules: ["S&A"] },
    "product_location_category_assortment": { category: "Drop 1 - Master Data", label: "Product-location-category-assortment", modules: ["S&A"] },
    "end_customers": { category: "Drop 1 - Master Data", label: "End customers", modules: ["F&R"] },
    "product_end_customers": { category: "Drop 1 - Master Data", label: "Product-end customers", modules: ["F&R"] },
    "product_images": { category: "Drop 1 - Master Data", label: "Product Images", modules: ["S&A"] },
    "floorplan_and_planogram_migrations": { category: "Drop 1 - Master Data", label: "Floorplan and planogram migrations", modules: ["S&A"] },
    "delivery_schedules": { category: "Drop 1 - Master Data", label: "Delivery schedules", modules: ["F&R"] },
    "product_batch_sizes": { category: "Drop 1 - Master Data", label: "Product-batch sizes", modules: ["F&R"] },
    "product_location_batch_sizes": { category: "Drop 1 - Master Data", label: "Product-location-batch sizes", modules: ["F&R"] },
    "product_location_suppliers": { category: "Drop 1 - Master Data", label: "Product-location-suppliers", modules: ["F&R", "P&P"] },
    "suppliers": { category: "Drop 1 - Master Data", label: "Suppliers", modules: ["F&R", "P&P"] },
    "commercial_suppliers": { category: "Drop 1 - Master Data", label: "Commercial suppliers", modules: ["P&P"] },
    "product_location_commercial_suppliers": { category: "Drop 1 - Master Data", label: "Product-location-commercial-suppliers", modules: ["P&P"] },
    "supplier_fundings": { category: "Drop 1 - Master Data", label: "Supplier fundings", modules: ["P&P"] },
    "merchandising_product_catalogues": { category: "Drop 1 - Master Data", label: "Merchandising product catalogues", modules: ["S&A"] },
    "floor_plans": { category: "Drop 1 - Master Data", label: "Floor-plans", modules: ["S&A"] },
    "location_category_spaces": { category: "Drop 1 - Master Data", label: "Location-category-spaces", modules: ["S&A"] },
    "planogram_positions": { category: "Drop 1 - Master Data", label: "Planogram-positions", modules: ["F&R", "S&A"] },
    "product_location_supplier_replenishment_agreements": { category: "Drop 1 - Master Data", label: "Product-location-supplier-replenishment agreements", modules: ["F&R"] },
    "replenishment_agreements": { category: "Drop 1 - Master Data", label: "Replenishment agreements", modules: ["F&R"] },
    "product_location_coordination_groups": { category: "Drop 1 - Master Data", label: "Product-location coordination groups", modules: ["F&R"] },
    "coordination_groups": { category: "Drop 1 - Master Data", label: "Coordination groups", modules: ["F&R"] },
    "constraint_groups": { category: "Drop 1 - Master Data", label: "Constraint groups", modules: ["F&R"] },
    "supplier_locations": { category: "Drop 1 - Master Data", label: "Supplier-locations", modules: ["F&R"] },
    "brackets": { category: "Drop 1 - Master Data", label: "Brackets", modules: ["F&R"] },
    "supplier_location_brackets": { category: "Drop 1 - Master Data", label: "Supplier-location brackets", modules: ["F&R"] },
    "coordination_group_brackets": { category: "Drop 1 - Master Data", label: "Coordination group brackets", modules: ["F&R"] },
    "product_replacements_and_refs": { category: "Drop 1 - Master Data", label: "Product replacements and refs", modules: ["F&R"] },
    "prepack_variants": { category: "Drop 1 - Master Data", label: "Prepack variants", modules: ["F&R"] },
    "supplier_closures": { category: "Drop 1 - Master Data", label: "Supplier closures", modules: ["F&R"] },
    "category_clusters": { category: "Drop 1 - Master Data", label: "Category-clusters", modules: ["S&A"] },
    "bill_of_materials": { category: "Drop 1 - Master Data", label: "Bill-of-materials", modules: ["F&R"] },
    "barcodes": { category: "Drop 1 - Master Data", label: "Barcodes", modules: [] },

    // Drop 2 - Historical Data
    "historical_balances": { category: "Drop 2 - Historical Data", label: "Historical balances", modules: ["F&R", "S&A", "P&P"] },
    "historical_sales": { category: "Drop 2 - Historical Data", label: "Historical sales", modules: ["F&R", "S&A", "P&P"] },
    "historical_deliveries": { category: "Drop 2 - Historical Data", label: "Historical deliveries", modules: ["F&R", "S&A"] },
    "historical_spoilages": { category: "Drop 2 - Historical Data", label: "Historical spoilages", modules: ["F&R", "S&A"] },
    "historical_transfers": { category: "Drop 2 - Historical Data", label: "Historical transfers", modules: ["F&R"] },
    "historical_inventory_checks": { category: "Drop 2 - Historical Data", label: "Historical inventory checks", modules: ["F&R"] },
    "historical_adjustments": { category: "Drop 2 - Historical Data", label: "Historical adjustments", modules: ["F&R"] },
    "historical_lost_sales": { category: "Drop 2 - Historical Data", label: "Historical lost sales", modules: ["F&R"] },
    "historical_campaigns": { category: "Drop 2 - Historical Data", label: "Historical campaigns", modules: ["F&R", "P&P"] },
    "historical_product_location_campaigns": { category: "Drop 2 - Historical Data", label: "Historical product-location-campaigns", modules: ["F&R", "P&P"] },
    "historical_price_calendars": { category: "Drop 2 - Historical Data", label: "Historical Price calendars", modules: ["F&R"] },
    "historical_sales_prices": { category: "Drop 2 - Historical Data", label: "Historical Sales prices", modules: ["P&P"] },
    "historical_purchase_prices": { category: "Drop 2 - Historical Data", label: "Historical Purchase prices", modules: ["P&P"] },
    "historical_supplier_fundings": { category: "Drop 2 - Historical Data", label: "Historical Supplier fundings", modules: ["P&P"] },
    "historical_promotion_assortments": { category: "Drop 2 - Historical Data", label: "Historical promotion assortments", modules: ["P&P"] },
    "historical_footfalls": { category: "Drop 2 - Historical Data", label: "Historical footfalls", modules: [] },

    // Drop 3 - Transactional Data
    "sales": { category: "Drop 3 - Transactional Data", label: "Sales", modules: ["F&R", "S&A", "P&P"] },
    "end_customer_transactions": { category: "Drop 3 - Transactional Data", label: "End customer transactions", modules: ["F&R"] },
    "balances": { category: "Drop 3 - Transactional Data", label: "Balances", modules: ["F&R", "S&A", "P&P"] },
    "batch_balances": { category: "Drop 3 - Transactional Data", label: "Batch balances", modules: ["F&R"] },
    "open_purchase_orders": { category: "Drop 3 - Transactional Data", label: "Open purchase orders", modules: ["F&R"] },
    "open_sales_orders": { category: "Drop 3 - Transactional Data", label: "Open sales orders", modules: ["F&R"] },
    "deliveries": { category: "Drop 3 - Transactional Data", label: "Deliveries", modules: ["F&R", "S&A"] },
    "spoilages": { category: "Drop 3 - Transactional Data", label: "Spoilages", modules: ["F&R", "S&A"] },
    "transfers": { category: "Drop 3 - Transactional Data", label: "Transfers", modules: ["F&R"] },
    "inventory_checks": { category: "Drop 3 - Transactional Data", label: "Inventory checks", modules: ["F&R"] },
    "adjustments": { category: "Drop 3 - Transactional Data", label: "Adjustments", modules: ["F&R"] },
    "lost_sales": { category: "Drop 3 - Transactional Data", label: "Lost sales", modules: ["F&R"] },
    "footfalls": { category: "Drop 3 - Transactional Data", label: "Footfalls", modules: [] },
    "dc_transactions": { category: "Drop 3 - Transactional Data", label: "DC transactions", modules: ["F&R"] },
    "orders": { category: "Drop 3 - Transactional Data", label: "Orders", modules: ["F&R"] },

    // Drop 4 - Output Interfaces
    "output_order_proposals": { category: "Drop 4 - Outbound", label: "Output: Order Proposals", modules: ["F&R"] },
    "output_reserve_order_proposals": { category: "Drop 4 - Outbound", label: "Output: Reserve Order Proposals", modules: ["F&R"] },
    "output_sales_forecasts": { category: "Drop 4 - Outbound", label: "Output: Sales Forecasts", modules: ["F&R", "S&A"] },
    "output_clearance": { category: "Drop 4 - Outbound", label: "Output: Clearance", modules: ["F&R"] },
    "output_workload_driver_forecast_day_level": { category: "Drop 4 - Outbound", label: "Output: Workload driver forecast - day level", modules: [] },
    "output_workload_driver_forecast_15_min_level": { category: "Drop 4 - Outbound", label: "Output: Workload driver forecast - 15 min level", modules: [] },
    "output_shelf_edge_labels": { category: "Drop 4 - Outbound", label: "Output: Shelf Edge Labels", modules: ["S&A"] },
    "output_inventory_exceptions": { category: "Drop 4 - Outbound", label: "Output: Inventory exceptions", modules: ["F&R"] },
    "output_promotions": { category: "Drop 4 - Outbound", label: "Output: Promotions", modules: ["P&P"] },
    "output_prices": { category: "Drop 4 - Outbound", label: "Output: Prices", modules: ["P&P"] },
    "output_assortments": { category: "Drop 4 - Outbound", label: "Output: Assortments", modules: ["S&A"] },
};

const DEFAULT_TASKS = ["Sample Delivery", "Technical Validation", "Full Extraction", "Connect Integration", "Upload in RELEX", "Functional Validation"];
const DROP_NAMES = ["Drop 1 - Master Data", "Drop 2 - Historical Data", "Drop 3 - Transactional Data", "Drop 4 - Outbound", "Drop 5 - Additional Data"];
// Manual drop limit. An imported Interface Document (or a saved schedule) with more drops raises it to match.
const MAX_DROPS = 5;

// A drop is named "Drop <n> - <label>". The "Drop <n>" prefix is fixed; only the label is editable.
const DROP_NAME_PATTERN = /^(Drop\s*\d+)\s*(?:-\s*(.*))?$/i;
const getDropPrefix = (name = '') => {
    const match = (name || '').match(DROP_NAME_PATTERN);
    return match ? match[1].replace(/\s+/g, ' ') : null;
};
const getDropLabel = (name = '') => {
    const match = (name || '').match(DROP_NAME_PATTERN);
    return match ? (match[2] || '') : (name || '');
};
const buildDropName = (prefix, label) => {
    if (!prefix) return label;
    return label.trim() ? `${prefix} - ${label}` : prefix;
};
// Drops past the defaults reuse the "Additional Data" label; it stays editable like any other.
const getStandardDropName = (dropNumber) => DROP_NAMES[dropNumber - 1] || `Drop ${dropNumber} - Additional Data`;
// The number in a drop's "Drop <n>" prefix, i.e. what the user sees. Saving renumbers drops by
// position, so drop_number can disagree with the name; it is only the fallback for unprefixed names.
const getDropNumber = (drop) => {
    const prefix = getDropPrefix(drop.name);
    return prefix ? parseInt(prefix.replace(/\D/g, ''), 10) : drop.drop_number;
};
// Drops are matched to interface categories by their "Drop <n>" prefix so a renamed
// drop is still recognised as the same slot as its default category name.
const isSameDropSlot = (nameA = '', nameB = '') => {
    const prefixA = getDropPrefix(nameA);
    const prefixB = getDropPrefix(nameB);
    if (prefixA && prefixB) return prefixA.toLowerCase() === prefixB.toLowerCase();
    return nameA === nameB;
};

// --- MultiUserSelect & Checkbox & CancelInterfaceModal & ReAuthModal ---
const MultiUserSelect = ({ allUsers, selectedUserIds, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useRef(null);
    const selectedUsers = useMemo(() => selectedUserIds.map(id => allUsers.find(u => u.id === id)).filter(Boolean), [selectedUserIds, allUsers]);
    const availableUsers = useMemo(() => allUsers.filter(u => !selectedUserIds.includes(u.id)), [selectedUserIds, allUsers]);
    useEffect(() => {
        const handleClickOutside = (event) => { if (selectRef.current && !selectRef.current.contains(event.target)) setIsOpen(false); };
        document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const handleRemoveUser = (userId) => onChange(selectedUserIds.filter(id => id !== userId));
    const handleAddUser = (userId) => { onChange([...selectedUserIds, userId]); setIsOpen(false); };
    return (
        <div className="multi-select-container" ref={selectRef}>
            <div className="selected-users-pills" onClick={() => setIsOpen(!isOpen)}>
                {selectedUsers.length > 0 ? selectedUsers.map(user => (<span key={user.id} className="user-pill">{user.username}<button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveUser(user.id); }}>&times;</button></span>)) : <span className="no-users-text">Unassigned</span>}
            </div>
            {isOpen && (<div className="user-dropdown-list">{availableUsers.length > 0 ? availableUsers.map(user => (<div key={user.id} className="user-dropdown-item" onClick={() => handleAddUser(user.id)}>{user.username}</div>)) : <div className="user-dropdown-item disabled">No more users to add</div>}</div>)}
        </div>
    );
};

const Checkbox = ({ id, name, checked, onChange, label, children, disabled }) => (
    <div className="checkbox-container">
        <div className="checkbox-wrapper">
            <input id={id} type="checkbox" name={name} checked={checked} onChange={onChange} className="checkbox-input" disabled={disabled} />
            <label htmlFor={id} className={`checkbox-label ${disabled ? 'disabled' : ''}`}>{label}</label>
        </div>
        {children}
    </div>
);

const CancelInterfaceModal = ({ isOpen, onClose, onConfirm, interfaceName, flatInterfaces }) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setReason('');
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!reason.trim()) {
            setError("Please provide a reason.");
            return;
        }
        onConfirm(reason);
    };

    const formattedName = getInterfaceLabel(interfaceName, flatInterfaces || []);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content cancel-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-body">
                    <div className="cancel-modal-icon-wrapper">
                        <FaExclamationTriangle className="cancel-modal-icon" />
                    </div>
                    <h3 className="cancel-modal-title">Cancel Interface</h3>
                    <p className="cancel-modal-text">
                        You are about to cancel the <strong>{formattedName}</strong> interface. This will deactivate it and remove it from the schedule.
                    </p>
                    <label htmlFor="cancel-reason" className="input-label">Please provide a reason for this change:</label>
                    <textarea
                        id="cancel-reason"
                        value={reason}
                        onChange={(e) => { setReason(e.target.value); if (error) setError(''); }}
                        rows="3"
                        placeholder="e.g., This interface is no longer in scope..."
                    ></textarea>
                    {error && <p className="form-feedback error small" style={{ marginTop: '0.75rem', marginBottom: 0 }}>{error}</p>}
                </div>
                <div className="modal-footer">
                    <button type="button" onClick={onClose} className="action-button secondary">Back</button>
                    <button type="button" onClick={handleConfirm} className="action-button danger">Confirm Cancellation</button>
                </div>
            </div>
        </div>
    );
};

const ReAuthModal = ({ isOpen, onClose, onConfirm, isLoading, error, projectName }) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    useEffect(() => { if (!isOpen) { setPassword(''); setShowPassword(false); } }, [isOpen]);
    if (!isOpen) return null;
    const handleSubmit = (e) => { e.preventDefault(); onConfirm(password); };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content auth-modal" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit}>
                    <div className="modal-header"><h3>Confirm Action</h3><button type="button" onClick={onClose} className="modal-close-button">&times;</button></div>
                    <div className="modal-body">
                        <p>Please enter your current password to confirm deletion of the project: <strong>{projectName}</strong></p>
                        {error && <p className="form-feedback error small">{error}</p>}
                        <div className="input-group"><label style={{ marginBottom: 1 + "rem" }} htmlFor="auth-password">Password</label><div className="password-input-container"><input type={showPassword ? 'text' : 'password'} id="auth-password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus /><button type="button" onClick={() => setShowPassword(!showPassword)} className="password-toggle-icon">{showPassword ? <FaEyeSlash /> : <FaEye />}</button></div></div>
                    </div>
                    <div className="modal-footer"><button type="button" onClick={onClose} className="action-button secondary" disabled={isLoading}>Cancel</button><button type="submit" className="action-button danger" disabled={isLoading}>{isLoading ? 'Verifying...' : 'Confirm Delete'}</button></div>
                </form>
            </div>
        </div>
    );
};

// --- Main Edit Project Page Component ---
export default function EditProjectFormPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    // DYNAMIC STATE FOR INTERFACES
    const [projectInterfaces, setProjectInterfaces] = useState({});
    const flatProjectInterfaces = useMemo(() => Object.values(projectInterfaces).flat(), [projectInterfaces]);

    const [projectName, setProjectName] = useState('');
    const [originalProjectName, setOriginalProjectName] = useState('');
    const [activeInterfaces, setActiveInterfaces] = useState({});
    const [drops, setDrops] = useState([]);
    const [dropLimit, setDropLimit] = useState(MAX_DROPS);
    const [cancelledInterfaces, setCancelledInterfaces] = useState(new Set());
    const [projectUsers, setProjectUsers] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [allMasterFields, setAllMasterFields] = useState({});
    const [selectedFields, setSelectedFields] = useState({});
    const [omissionReasons, setOmissionReasons] = useState({});
    const [mandatoryOverrides, setMandatoryOverrides] = useState({});

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInterface, setEditingInterface] = useState(null);

    const [isCustomInterfaceModalOpen, setIsCustomInterfaceModalOpen] = useState(false);
    const [isCreatingCustomInterface, setIsCreatingCustomInterface] = useState(false);

    const [loading, setLoading] = useState(true);
    const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [cancellingInterface, setCancellingInterface] = useState(null);
    const [activeCategory, setActiveCategory] = useState("Drop 1 - Master Data");
    const [activeDropId, setActiveDropId] = useState(null);
    const [expandedInterfaces, setExpandedInterfaces] = useState({});
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [authError, setAuthError] = useState('');

    const [dateClipboard, setDateClipboard] = useState(null);

    // --- MODULE FILTERS STATE ---
    const [moduleFilters, setModuleFilters] = useState({
        'F&R': false,
        'S&A': false,
        'P&P': false
    });

    const toggleModuleFilter = (mod) => {
        setModuleFilters(prev => ({ ...prev, [mod]: !prev[mod] }));
    };

    const activeModuleList = Object.keys(moduleFilters).filter(m => moduleFilters[m]);

    const [toastInfo, setToastInfo] = useState({ message: '', id: 0 });
    const [interfaceSearchTerm, setInterfaceSearchTerm] = useState('');
    const [delayedSortState, setDelayedSortState] = useState({});
    const categoryContentRef = useRef(null);

    const [isParsingExcel, setIsParsingExcel] = useState(false);
    const [importedCustomFieldIds, setImportedCustomFieldIds] = useState([]);
    const [importedCustomInterfaceIds, setImportedCustomInterfaceIds] = useState([]);
    const excelInputRef = useRef(null);
    const [incompleteFieldsWarning, setIncompleteFieldsWarning] = useState(0);
    const [emptyInterfacesWarning, setEmptyInterfacesWarning] = useState(0);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [showImportWarningModal, setShowImportWarningModal] = useState(false);
    const [unschedulingInterface, setUnschedulingInterface] = useState(null);
    const [pastingToDropId, setPastingToDropId] = useState(null);
    const [removingDropId, setRemovingDropId] = useState(null);
    const [deletingCustomInterface, setDeletingCustomInterface] = useState(null);
    const [pageNotice, setPageNotice] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);

    const [pendingCancellations, setPendingCancellations] = useState({});
    const [pendingUncancellations, setPendingUncancellations] = useState(new Set());

    // --- AUTO-SAVE USEEFFECT ---
    useEffect(() => {
        if (isInitialLoadDone) {
            const draftState = {
                projectName,
                activeInterfaces,
                drops,
                dropLimit,
                assignedUsers,
                selectedFields,
                activeCategory,
                activeDropId,
                expandedInterfaces,
                pendingCancellations,
                pendingUncancellations: Array.from(pendingUncancellations),
                cancelledInterfaces: Array.from(cancelledInterfaces),
                omissionReasons,
                mandatoryOverrides,
                importedCustomFieldIds,
                importedCustomInterfaceIds
            };
            sessionStorage.setItem(`wysupp_edit_project_draft_${projectId}`, JSON.stringify(draftState));
        }
    }, [projectName, activeInterfaces, drops, dropLimit, assignedUsers, selectedFields, activeCategory, activeDropId, expandedInterfaces, pendingCancellations, pendingUncancellations, cancelledInterfaces, omissionReasons, mandatoryOverrides, importedCustomFieldIds, importedCustomInterfaceIds, isInitialLoadDone, projectId]);

    useEffect(() => {
        if (toastInfo.message) {
            const timer = setTimeout(() => setToastInfo({ message: '', id: 0 }), 3000);
            return () => clearTimeout(timer);
        }
    }, [toastInfo]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (
                categoryContentRef.current &&
                document.activeElement &&
                categoryContentRef.current.contains(document.activeElement) &&
                !document.activeElement.classList.contains('interface-search-input')
            ) {
                document.activeElement.blur();
            }
            setDelayedSortState(activeInterfaces);
        }, 500);
        return () => clearTimeout(timer);
    }, [activeInterfaces]);

    const groupedMasterFields = useMemo(() => {
        const grouped = {};
        Object.entries(allMasterFields).forEach(([interfaceName, fields]) => {
            if (!Array.isArray(fields)) {
                grouped[interfaceName] = {};
                return;
            }
            grouped[interfaceName] = fields.reduce((acc, field) => {
                const fileName = field.source_file || 'Standard Fields';
                if (!acc[fileName]) {
                    acc[fileName] = [];
                }
                acc[fileName].push(field);
                return acc;
            }, {});
        });
        return grouped;
    }, [allMasterFields]);

    const activeDrop = useMemo(() => drops.find(d => d.id === activeDropId), [drops, activeDropId]);

    const groupedTasks = useMemo(() => {
        if (!activeDrop) return {};
        return activeDrop.tasks.reduce((acc, task) => {
            const key = task.interface_name;
            if (!acc[key]) acc[key] = [];
            acc[key].push(task);
            return acc;
        }, {});
    }, [activeDrop]);

    const unassignedInterfaces = useMemo(() => {
        const interfacesInDrops = new Set(drops.flatMap(d => d.tasks.map(t => t.interface_name)));
        const allActiveKeys = Object.keys(activeInterfaces).filter(key => activeInterfaces[key]);

        return allActiveKeys.filter(key =>
            !interfacesInDrops.has(key) &&
            !cancelledInterfaces.has(key) &&
            !pendingCancellations[key]
        ).map(key => ({
            key: key,
            label: getInterfaceLabel(key, flatProjectInterfaces)
        }));
    }, [activeInterfaces, drops, cancelledInterfaces, pendingCancellations, flatProjectInterfaces]);

    // --- RESET CHANGES HANDLER ---
    const handleDiscardChanges = () => {
        setShowDiscardModal(true);
    };

    const confirmDiscardChanges = async () => {
        if (importedCustomFieldIds.length > 0 || importedCustomInterfaceIds.length > 0) {
            try {
                await axiosInstance.post('/api/master-interface-fields/cleanup', {
                    field_ids: importedCustomFieldIds,
                    interface_ids: importedCustomInterfaceIds
                });
            } catch (e) {
                console.error("Failed to clean up orphaned import data:", e);
            }
        }
        sessionStorage.removeItem(`wysupp_edit_project_draft_${projectId}`);
        window.location.reload();
    };

    // --- IMPORT FROM INTERFACE DOCUMENT ---
    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsParsingExcel(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);
        // Scopes the parser to this project so its own custom fields/interfaces are matched
        // and reused instead of being re-created as duplicates on every re-import.
        if (projectId) formData.append('project_id', projectId);

        try {
            const response = await axiosInstance.post('/api/projects/parse-interface-document', formData);
            const { importedActiveInterfaces, importedInterfaceDrops, importedSelectedFields, newlyCreatedFields, newlyCreatedInterfaces } = response.data;

            if (newlyCreatedInterfaces && newlyCreatedInterfaces.length > 0) {
                const newIfaceIds = newlyCreatedInterfaces.map(i => i.id);
                setImportedCustomInterfaceIds(prev => [...prev, ...newIfaceIds]);

                setProjectInterfaces(prev => {
                    const next = { ...prev };
                    const drop5Name = "Drop 5 - Additional Data";
                    if (!next[drop5Name]) next[drop5Name] = [];
                    newlyCreatedInterfaces.forEach(i => {
                        if (!next[drop5Name].find(x => x.key === i.key)) {
                            next[drop5Name].push({ key: i.key, label: i.label, modules: ["Custom"], isCustom: true });
                        }
                    });
                    return next;
                });
            }

            if (newlyCreatedFields && newlyCreatedFields.length > 0) {
                const newIds = newlyCreatedFields.map(f => f.id);
                setImportedCustomFieldIds(prev => [...prev, ...newIds]);

                setAllMasterFields(prev => {
                    const updated = { ...prev };
                    newlyCreatedFields.forEach(field => {
                        if (!updated[field.interface_name]) updated[field.interface_name] = [];
                        if (!updated[field.interface_name].find(f => f.id === field.id)) {
                            updated[field.interface_name].push(field);
                        }
                    });
                    return updated;
                });

                let incompleteCount = 0;
                newlyCreatedFields.forEach(field => {
                    const isPerfect =
                        field.file_header_name?.trim() &&
                        field.data_type?.trim() &&
                        field.table_in_relex?.trim() &&
                        field.ui_field_in_relex?.trim() &&
                        field.used_for?.trim() &&
                        field.description?.trim() &&
                        field.example_value?.trim();
                    if (!isPerfect) incompleteCount++;
                });
                if (incompleteCount > 0) setIncompleteFieldsWarning(incompleteCount);
            }

            if (newlyCreatedInterfaces && newlyCreatedInterfaces.length > 0) {
                const emptyCount = newlyCreatedInterfaces.filter(
                    iface => !importedSelectedFields[iface.key] || importedSelectedFields[iface.key].length === 0
                ).length;
                if (emptyCount > 0) setEmptyInterfacesWarning(emptyCount);
            }

            // Build the full interface map including any newly created custom interfaces
            const latestProjectInterfaces = { ...projectInterfaces };
            if (newlyCreatedInterfaces && newlyCreatedInterfaces.length > 0) {
                const drop5Name = "Drop 5 - Additional Data";
                if (!latestProjectInterfaces[drop5Name]) latestProjectInterfaces[drop5Name] = [];
                newlyCreatedInterfaces.forEach(i => {
                    if (!latestProjectInterfaces[drop5Name].find(x => x.key === i.key)) {
                        latestProjectInterfaces[drop5Name].push({ key: i.key, label: i.label, modules: ["Custom"], isCustom: true });
                    }
                });
            }

            // Cancelled interfaces (saved or pending) are left exactly as they are: the document can't
            // reactivate or schedule them. They become importable once uncancelled in section 2.
            const cancelledKeys = new Set([...cancelledInterfaces, ...Object.keys(pendingCancellations)]);
            const keepCancelledAsIs = (next, prev) => {
                cancelledKeys.forEach(k => {
                    if (k in prev) next[k] = prev[k];
                    else delete next[k];
                });
                return next;
            };

            // Replace: deactivate all known interfaces, then activate only what the document has
            setActiveInterfaces(prev => keepCancelledAsIs({
                ...Object.keys(prev).reduce((acc, k) => ({ ...acc, [k]: false }), {}),
                ...importedActiveInterfaces
            }, prev));

            // Replace selected fields entirely — document is the new source of truth
            setSelectedFields(prev => keepCancelledAsIs({ ...importedSelectedFields }, prev));

            // Pending cancellations/uncancellations are kept: only Uncancel undoes a cancellation, and an
            // uncancel made before the import still has to reach the server when the project is saved.

            // Rebuild drops from scratch — only interfaces present in the document
            const activatedKeys = Object.keys(importedActiveInterfaces).filter(k => importedActiveInterfaces[k] && !cancelledKeys.has(k));
            const skippedCancelledKeys = Object.keys(importedActiveInterfaces).filter(k => importedActiveInterfaces[k] && cancelledKeys.has(k));
            let newDrops = [];
            activatedKeys.forEach(key => {
                // The document's "Interface summary" tab decides the drop. Interfaces it
                // doesn't number fall back to the drop of their default category.
                const docDropNumber = importedInterfaceDrops?.[key];
                let targetCategory = docDropNumber ? getStandardDropName(docDropNumber) : null;
                if (!targetCategory) {
                    for (const [categoryName, interfacesArray] of Object.entries(latestProjectInterfaces)) {
                        if (interfacesArray.some(int => int.key === key)) {
                            targetCategory = categoryName;
                            break;
                        }
                    }
                }
                if (!targetCategory) return;

                const newTasks = DEFAULT_TASKS.map(taskName => ({
                    id: `new-${key}-${taskName}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                    interface_name: key,
                    task_name: taskName,
                    start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
                }));

                const targetDropIndex = newDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));
                if (targetDropIndex >= 0) {
                    newDrops[targetDropIndex] = {
                        ...newDrops[targetDropIndex],
                        tasks: [...newDrops[targetDropIndex].tasks, ...newTasks]
                    };
                } else {
                    const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                    const dropNumber = docDropNumber || (dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : newDrops.length + 1);
                    newDrops.push({
                        id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        name: targetCategory,
                        drop_number: dropNumber,
                        tasks: newTasks
                    });
                }
            });
            newDrops.sort((a, b) => a.drop_number - b.drop_number);
            setDrops(newDrops);
            // The import replaces the schedule, so the limit follows the new drops rather than the old ones.
            setDropLimit(Math.max(MAX_DROPS, ...newDrops.map(getDropNumber)));

            // Expand all imported interfaces in the schedule view
            setExpandedInterfaces(activatedKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}));
            setToastInfo({ message: `Document parsed! Imported active interfaces and ${newlyCreatedFields?.length || 0} custom fields.`, id: Date.now() });

            if (skippedCancelledKeys.length > 0) {
                const skippedLabels = skippedCancelledKeys.map(k => getInterfaceLabel(k, flatProjectInterfaces)).join(', ');
                setPageNotice({
                    tone: 'warning',
                    title: 'Cancelled Interfaces Not Scheduled',
                    message: `The document includes ${skippedCancelledKeys.length} interface(s) that are cancelled in this project, so they were left out of the schedule: ${skippedLabels}. To import them, uncancel them in section 2 and import the document again.`
                });
            }

        } catch (err) {
            if (err.response?.status === 401) return;
            setError(err.response?.data?.detail || 'Failed to parse the Interface Document. Please ensure it follows the RELEX standard format.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsParsingExcel(false);
            if (excelInputRef.current) excelInputRef.current.value = null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Unclaimed custom interfaces/fields are hidden from the API by default so an
                // abandoned import can't leak into everyone's form. Ask for the ones this
                // draft created back, so a page refresh mid-import keeps them visible.
                let savedDraft = null;
                try {
                    const savedDraftStr = sessionStorage.getItem(`wysupp_edit_project_draft_${projectId}`);
                    if (savedDraftStr) savedDraft = JSON.parse(savedDraftStr);
                } catch (e) {
                    console.error("Invalid draft", e);
                }
                const pendingInterfaceIds = (savedDraft?.importedCustomInterfaceIds || []).join(',');
                const pendingFieldIds = (savedDraft?.importedCustomFieldIds || []).join(',');

                // Fetch project data AND the dynamic master interfaces list simultaneously
                const [projectRes, masterFieldsRes, masterInterfacesRes] = await Promise.all([
                    axiosInstance.get(`/api/projects/${projectId}`),
                    axiosInstance.get(`/api/master-interface-fields`, { params: { project_id: projectId, pending_ids: pendingFieldIds || undefined } }),
                    axiosInstance.get(`/api/master-interfaces/`, { params: { project_id: projectId, pending_ids: pendingInterfaceIds || undefined } })
                ]);

                const projectData = projectRes.data;
                const projectNameFromRes = projectData.project_name;

                setOriginalProjectName(projectNameFromRes);

                const fetchedMasterFields = masterFieldsRes.data.interfaces || {};
                setAllMasterFields(fetchedMasterFields);

                // --- DYNAMIC INTERFACE GENERATION ---
                const fetchedInterfaces = masterInterfacesRes.data || [];
                let currentInterfaces = {};
                let currentFlat = [];

                fetchedInterfaces.forEach(iface => {
                    const meta = INTERFACE_METADATA[iface.interface_name];
                    const category = meta ? meta.category : (iface.interface_drop || "Drop 5 - Additional Data");

                    const processed = {
                        key: iface.interface_name,
                        label: meta ? meta.label : iface.interface_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                        modules: meta ? meta.modules : [],
                        isCustom: !!iface.is_custom
                    };

                    if (!currentInterfaces[category]) currentInterfaces[category] = [];
                    currentInterfaces[category].push(processed);
                    currentFlat.push(processed);
                });

                // Enforce strict drop ordering
                const sortedGrouped = {};
                DROP_NAMES.forEach(dropName => {
                    if (currentInterfaces[dropName]) sortedGrouped[dropName] = currentInterfaces[dropName];
                });
                Object.keys(currentInterfaces).forEach(key => {
                    if (!sortedGrouped[key]) sortedGrouped[key] = currentInterfaces[key];
                });
                currentInterfaces = sortedGrouped;

                if (projectNameFromRes) {
                    const [scheduleResponse, usersResponse, statusesResponse, fieldAssocRes, customInterfacesRes] = await Promise.all([
                        axiosInstance.get(`/api/projects/${projectNameFromRes}/schedule?include_inactive=true`),
                        axiosInstance.get(`/api/projects/${projectNameFromRes}/users`),
                        axiosInstance.get(`/api/projects/${projectNameFromRes}/interfaces`),
                        axiosInstance.get(`/api/projects/${projectId}/field-associations`),
                        axiosInstance.get(`/api/projects/${projectNameFromRes}/custom-interfaces`).catch(err => {
                            console.warn("Could not fetch custom interfaces list directly.", err);
                            return { data: [] };
                        })
                    ]);

                    const customInterfacesData = customInterfacesRes.data || [];
                    customInterfacesData.forEach(ci => {
                        const dropName = ci.interface_drop || 'Drop 5 - Additional Data';
                        const ifaceKey = ci.interface_name;

                        if (!currentInterfaces[dropName]) {
                            currentInterfaces[dropName] = [];
                        }
                        if (!currentFlat.find(i => i.key === ifaceKey)) {
                            const newItem = {
                                key: ifaceKey,
                                label: getInterfaceLabel(ifaceKey, currentFlat) || ci.interface_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                                isCustom: true
                            };
                            currentInterfaces[dropName].push(newItem);
                            currentFlat.push(newItem);
                        }
                    });

                    const initialActive = currentFlat.reduce((acc, int) => ({ ...acc, [int.key]: projectData[int.key] || false }), {});

                    const scheduleData = scheduleResponse.data;

                    scheduleData.forEach(drop => {
                        const dropName = drop.name;
                        if (!currentInterfaces[dropName]) {
                            currentInterfaces[dropName] = [];
                        }
                        drop.tasks.forEach(task => {
                            const ifaceKey = task.interface_name;
                            if (!currentFlat.find(i => i.key === ifaceKey)) {
                                const newItem = { key: ifaceKey, label: getInterfaceLabel(ifaceKey, currentFlat), isCustom: true };
                                currentInterfaces[dropName].push(newItem);
                                currentFlat.push(newItem);
                            }
                            initialActive[ifaceKey] = true;
                        });
                    });

                    const { field_selections, mandatory_overrides, omission_reasons } = fieldAssocRes.data;
                    const fieldIdsByInterface = fetchedMasterFields;
                    const initialSelectedFields = {};
                    Object.keys(fieldIdsByInterface).forEach(iface => { initialSelectedFields[iface] = []; });

                    if (field_selections) {
                        Object.keys(field_selections).forEach(fieldIdStr => {
                            const fieldId = parseInt(fieldIdStr, 10);
                            const isSelected = field_selections[fieldId];
                            if (isSelected) {
                                for (const iface in fieldIdsByInterface) {
                                    if (fieldIdsByInterface[iface].some(field => field.id === fieldId)) {
                                        initialSelectedFields[iface].push(fieldId);
                                        break;
                                    }
                                }
                            }
                        });
                    }

                    const allInterfaceNamesInSchedule = new Set(scheduleData.flatMap(drop => drop.tasks.map(task => task.interface_name)));
                    const initialExpandedState = {};
                    allInterfaceNamesInSchedule.forEach(name => { initialExpandedState[name] = true; });

                    const statusesData = statusesResponse.data;
                    const initiallyCancelled = new Set(statusesData.filter(s => s.cancellation_reason).map(s => s.interface_name));
                    const usersData = usersResponse.data;

                    const scheduleWithUserIds = scheduleData.map(drop => ({
                        ...drop,
                        tasks: drop.tasks.map(task => ({
                            ...task,
                            responsible_user_ids: task.responsible_users.map(user => user.id)
                        }))
                    }));

                    const processedDrops = scheduleWithUserIds.map(drop => {
                        const interfacesInDrop = new Map();
                        drop.tasks.forEach(task => {
                            if (!interfacesInDrop.has(task.interface_name)) interfacesInDrop.set(task.interface_name, []);
                            interfacesInDrop.get(task.interface_name).push(task);
                        });
                        interfacesInDrop.forEach((existingTasks, interfaceName) => {
                            const existingTaskNames = new Set(existingTasks.map(t => t.task_name));
                            DEFAULT_TASKS.forEach(defaultTaskName => {
                                if (!existingTaskNames.has(defaultTaskName)) {
                                    existingTasks.push({ id: `new-${interfaceName}-${defaultTaskName}-${Date.now()}`, interface_name: interfaceName, task_name: defaultTaskName, start_date: null, end_date: null, status: 'Pending', responsible_user_ids: [] });
                                }
                            });
                            existingTasks.sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name));
                        });
                        return { ...drop, tasks: Array.from(interfacesInDrop.values()).flat() };
                    });

                    // --- DRAFT RESTORATION LOGIC ---
                    const draftStr = sessionStorage.getItem(`wysupp_edit_project_draft_${projectId}`);
                    let draft = null;
                    if (draftStr) {
                        try { draft = JSON.parse(draftStr); } catch (e) { console.error("Invalid draft", e); }
                    }

                    if (draft) {
                        setProjectName(draft.projectName);
                        setActiveInterfaces(draft.activeInterfaces);
                        setDrops(draft.drops);
                        setDropLimit(draft.dropLimit || Math.max(MAX_DROPS, ...(draft.drops || []).map(getDropNumber)));
                        setAssignedUsers(draft.assignedUsers);
                        setSelectedFields(draft.selectedFields);
                        setActiveCategory(draft.activeCategory || Object.keys(currentInterfaces)[0] || "Drop 1 - Master Data");
                        setActiveDropId(draft.activeDropId);
                        setExpandedInterfaces(draft.expandedInterfaces);
                        setPendingCancellations(draft.pendingCancellations || {});
                        setPendingUncancellations(new Set(draft.pendingUncancellations || []));
                        setCancelledInterfaces(new Set(draft.cancelledInterfaces || []));
                        setOmissionReasons(draft.omissionReasons || {});
                        setMandatoryOverrides(draft.mandatoryOverrides || {});
                        setImportedCustomFieldIds(draft.importedCustomFieldIds || []);
                        setImportedCustomInterfaceIds(draft.importedCustomInterfaceIds || []);

                        setProjectInterfaces(currentInterfaces);
                        setProjectUsers(usersData);
                        setToastInfo({ message: 'Restored unsaved changes from your previous session.', id: Date.now() });
                    } else {
                        setProjectName(projectNameFromRes);
                        setActiveInterfaces(initialActive);
                        setSelectedFields(initialSelectedFields);
                        setMandatoryOverrides(mandatory_overrides || {});
                        setOmissionReasons(omission_reasons || {});
                        setExpandedInterfaces(initialExpandedState);
                        setCancelledInterfaces(initiallyCancelled);
                        setAssignedUsers(usersData);
                        setDrops(processedDrops);
                        // A project saved from a document with more than 5 drops keeps that limit.
                        setDropLimit(Math.max(MAX_DROPS, ...processedDrops.map(getDropNumber)));

                        setActiveCategory(Object.keys(currentInterfaces)[0] || "Drop 1 - Master Data");
                        setProjectInterfaces(currentInterfaces);
                        setProjectUsers(usersData);
                    }

                } else {
                    setError("Could not retrieve project name.");
                }

                setIsInitialLoadDone(true);
                setLoading(false);
            } catch (err) {
                if (err.response?.status === 401) return;

                if (err.response && err.response.status === 403) {
                    setAccessDenied(true);
                    setLoading(false);
                    return;
                }
                setError(err.response?.data?.detail || err.message || "Failed to load project data.");
                setLoading(false);
            }
        };

        if (projectId) {
            fetchData();
        }
    }, [projectId, navigate]);

    useEffect(() => {
        if (!activeDropId && drops.length > 0) setActiveDropId(drops[0].id);
        if (activeDropId && !drops.some(d => d.id === activeDropId)) setActiveDropId(drops.length > 0 ? drops[0].id : null);
    }, [drops, activeDropId]);

    useEffect(() => {
        if (userSearchTerm.length < 2) { setUserSearchResults([]); return; }
        const fetchUsers = async () => {
            setIsSearching(true);
            try {
                const response = await axiosInstance.get(`/api/v1/users/autocomplete?q=${userSearchTerm}`);
                const assignedIds = new Set(assignedUsers.map(u => u.id));
                setUserSearchResults(response.data.filter(u => !assignedIds.has(u.id)));
                setIsSearching(false);
            } catch (err) {
                if (err.response?.status === 401) return;
                console.error("Failed to search users:", err);
                setIsSearching(false);
            }
        };
        const debounce = setTimeout(fetchUsers, 300);
        return () => clearTimeout(debounce);
    }, [userSearchTerm, assignedUsers]);

    const toggleInterfaceExpansion = (interfaceName) => setExpandedInterfaces(prev => ({ ...prev, [interfaceName]: !prev[interfaceName] }));
    const handleAddUser = (user) => { setAssignedUsers(prev => [...prev, user]); setUserSearchTerm(''); setUserSearchResults([]); };
    const handleRemoveUser = (userId) => setAssignedUsers(prev => prev.filter(user => user.id !== userId));

    const handleFieldAdded = async (newFields, interfaceName) => {
        try {
            const response = await axiosInstance.get(`/api/master-interface-fields?project_id=${projectId}`);
            const updatedFields = response.data.interfaces || {};
            setAllMasterFields(updatedFields);

            const fieldsToAdd = Array.isArray(newFields) ? newFields : [newFields];
            const newIds = fieldsToAdd.map(f => f.id);

            if (newIds.length > 0) {
                setSelectedFields(prev => {
                    const current = prev[interfaceName] || [];
                    const merged = Array.from(new Set([...current, ...newIds]));
                    return { ...prev, [interfaceName]: merged };
                });
            }
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error("Failed to fetch updated fields:", err);
        }
    };

    const handleFieldUpdated = (updatedField, interfaceName) => { setAllMasterFields(prev => { const newFieldsForInterface = (prev[interfaceName] || []).map(field => field.id === updatedField.id ? updatedField : field); return { ...prev, [interfaceName]: newFieldsForInterface }; }); };
    const handleFieldDeleted = (deletedFieldId, interfaceName) => { setAllMasterFields(prev => { const newFieldsForInterface = (prev[interfaceName] || []).filter(field => field.id !== deletedFieldId); return { ...prev, [interfaceName]: newFieldsForInterface }; }); setSelectedFields(prev => { const newSelectedForInterface = (prev[interfaceName] || []).filter(id => id !== deletedFieldId); return { ...prev, [interfaceName]: newSelectedForInterface }; }); setMandatoryOverrides(prev => { const newOverrides = { ...prev }; delete newOverrides[deletedFieldId]; return newOverrides; }); setOmissionReasons(prev => { const newReasons = { ...prev }; delete newReasons[deletedFieldId]; return newReasons; }); };

    const handleInterfaceCheckboxChange = (key) => {
        const isNowActive = !activeInterfaces[key];
        setActiveInterfaces(prev => ({ ...prev, [key]: isNowActive }));

        if (isNowActive) {
            const ifaceLabel = getInterfaceLabel(key, flatProjectInterfaces);
            setToastInfo({ message: `"${ifaceLabel}" added to project!`, id: Date.now() });

            let targetCategory = null;
            for (const [categoryName, interfacesArray] of Object.entries(projectInterfaces)) {
                if (interfacesArray.some(int => int.key === key)) {
                    targetCategory = categoryName;
                    break;
                }
            }

            if (targetCategory) {
                setDrops(prevDrops => {
                    let updatedDrops = [...prevDrops];
                    let targetDropIndex = updatedDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));

                    const newTasks = DEFAULT_TASKS.map(taskName => ({
                        id: `new-${key}-${taskName}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        interface_name: key,
                        task_name: taskName,
                        start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
                    }));

                    if (targetDropIndex >= 0) {
                        const existingDrop = updatedDrops[targetDropIndex];
                        updatedDrops[targetDropIndex] = {
                            ...existingDrop,
                            tasks: [...existingDrop.tasks, ...newTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
                        };
                    } else {
                        const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                        const dropNumber = dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : updatedDrops.length + 1;

                        updatedDrops.push({
                            id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                            name: targetCategory,
                            drop_number: dropNumber,
                            tasks: newTasks
                        });
                        updatedDrops.sort((a, b) => a.drop_number - b.drop_number);
                    }
                    return updatedDrops;
                });

                setExpandedInterfaces(prev => ({ ...prev, [key]: true }));
            }
        }

        if (isNowActive) {
            const allFieldIdsForInterface = (allMasterFields[key] || []).map(field => field.id);
            const initialSelection = allFieldIdsForInterface.filter(id => {
                const field = allMasterFields[key].find(f => f.id === id);
                return field.is_mandatory && mandatoryOverrides[id] !== false;
            });
            setSelectedFields(prev => ({ ...prev, [key]: initialSelection }));
            setOmissionReasons(prev => {
                const newReasons = { ...prev };
                allFieldIdsForInterface.forEach(id => { delete newReasons[id]; });
                return newReasons;
            });
            setMandatoryOverrides(prev => {
                const newOverrides = { ...prev };
                allFieldIdsForInterface.forEach(id => { delete newOverrides[id]; });
                return newOverrides;
            });

            if (cancelledInterfaces.has(key)) {
                setCancelledInterfaces(prev => { const newSet = new Set(prev); newSet.delete(key); return newSet; });
            }
        } else {
            setSelectedFields(prev => { const newSelected = { ...prev }; delete newSelected[key]; return newSelected; });
            setDrops(prevDrops => prevDrops.map(drop => ({ ...drop, tasks: drop.tasks.filter(t => t.interface_name !== key) })).filter(drop => drop.tasks.length > 0 || drop.id.toString().startsWith('new-drop')));
            const fieldsForInterface = (allMasterFields[key] || []).map(f => f.id);
            setOmissionReasons(prev => { const newReasons = { ...prev }; fieldsForInterface.forEach(fieldId => { delete newReasons[fieldId]; }); return newReasons; });
            setMandatoryOverrides(prev => { const newOverrides = { ...prev }; fieldsForInterface.forEach(fieldId => { delete newOverrides[fieldId]; }); return newOverrides; });
            setPendingCancellations(prev => { const newPending = { ...prev }; delete newPending[key]; return newPending; });
        }
    };

    const handleFieldSelectionChange = (interfaceName, newSelectedIds) => setSelectedFields(prev => ({ ...prev, [interfaceName]: newSelectedIds }));

    const handleReasonChange = (interfaceName, fieldId, reason) => {
        setOmissionReasons(prev => {
            const newReasons = { ...prev };
            if (reason !== null && reason !== undefined) {
                newReasons[fieldId] = reason;
            } else {
                delete newReasons[fieldId];
            }
            return newReasons;
        });
    };

    const handleMandatoryOverrideChange = (interfaceName, fieldId, overrideValue) => {
        setMandatoryOverrides(prev => {
            const newOverrides = { ...prev };
            if (overrideValue !== null) {
                newOverrides[fieldId] = overrideValue;
            } else {
                delete newOverrides[fieldId];
            }
            return newOverrides;
        });
    };

    const handleCopyDates = (dropId, sourceInterfaceName) => {
        const drop = drops.find(d => d.id === dropId);
        if (!drop) return;

        const sourceTasks = drop.tasks.filter(t => t.interface_name === sourceInterfaceName);
        const dateMap = sourceTasks.reduce((acc, task) => {
            acc[task.task_name] = {
                start_date: task.start_date,
                end_date: task.end_date,
                responsible_user_ids: task.responsible_user_ids
            };
            return acc;
        }, {});

        setDateClipboard(dateMap);
        const ifaceLabel = getInterfaceLabel(sourceInterfaceName, flatProjectInterfaces);
        setToastInfo({ message: `Copied dates & users from ${ifaceLabel}`, id: Date.now() });
    };

    const handlePasteDates = (dropId, targetInterfaceName) => {
        if (!dateClipboard) {
            setToastInfo({ message: 'Clipboard is empty. Copy dates & users first.', id: Date.now() });
            return;
        }

        setDrops(prevDrops => {
            return prevDrops.map(drop => {
                if (drop.id !== dropId) return drop;

                const newTasks = drop.tasks.map(task => {
                    if (task.interface_name === targetInterfaceName) {
                        const copiedData = dateClipboard[task.task_name];
                        if (copiedData) {
                            return {
                                ...task,
                                start_date: copiedData.start_date,
                                end_date: copiedData.end_date,
                                responsible_user_ids: copiedData.responsible_user_ids
                            };
                        }
                    }
                    return task;
                });
                return { ...drop, tasks: newTasks };
            });
        });

        const ifaceLabel = getInterfaceLabel(targetInterfaceName, flatProjectInterfaces);
        setToastInfo({ message: `Pasted dates & users to ${ifaceLabel}`, id: Date.now() });
    };

    const handlePasteDatesToDrop = (dropId) => {
        if (!dateClipboard) {
            setToastInfo({ message: 'Clipboard is empty. Copy dates & users first.', id: Date.now() });
            return;
        }

        const drop = drops.find(d => d.id === dropId);
        if (!drop || drop.tasks.length === 0) return;

        setPastingToDropId(dropId);
    };

    const confirmPasteDatesToDrop = () => {
        const dropId = pastingToDropId;
        setPastingToDropId(null);

        const drop = drops.find(d => d.id === dropId);
        if (!drop || !dateClipboard) return;

        setDrops(prevDrops => {
            return prevDrops.map(d => {
                if (d.id !== dropId) return d;

                const newTasks = d.tasks.map(task => {
                    const copiedData = dateClipboard[task.task_name];
                    if (copiedData) {
                        return {
                            ...task,
                            start_date: copiedData.start_date,
                            end_date: copiedData.end_date,
                            responsible_user_ids: copiedData.responsible_user_ids
                        };
                    }
                    return task;
                });
                return { ...d, tasks: newTasks };
            });
        });

        setToastInfo({ message: `Pasted dates & users to all interfaces in ${drop.name}`, id: Date.now() });
    };

    const handleUnscheduleInterface = (dropId, interfaceName) => {
        setUnschedulingInterface({ dropId, interfaceName });
    };

    const confirmUnscheduleInterface = () => {
        if (!unschedulingInterface) return;
        const { dropId, interfaceName } = unschedulingInterface;
        const ifaceLabel = getInterfaceLabel(interfaceName, flatProjectInterfaces);

        setDrops(prevDrops => {
            return prevDrops.map(drop => {
                if (drop.id !== dropId) return drop;
                const newTasks = drop.tasks.filter(task => task.interface_name !== interfaceName);
                return { ...drop, tasks: newTasks };
            });
        });

        setExpandedInterfaces(prev => {
            const newState = { ...prev };
            delete newState[interfaceName];
            return newState;
        });

        setUnschedulingInterface(null);
        setToastInfo({ message: `Interface "${ifaceLabel}" moved to "Available to Schedule".`, id: Date.now() });
    };

    const handleOpenModal = (interfaceName) => { setEditingInterface(interfaceName); setIsModalOpen(true); };
    const handleCloseModal = () => { setIsModalOpen(false); setEditingInterface(null); };
    const handleAddDrop = () => {
        if (drops.length < dropLimit) {
            // Take the lowest free drop number so re-adding a removed drop never repeats an existing one.
            const usedNumbers = new Set(drops.map(getDropNumber));
            let dropNumber = 1;
            while (usedNumbers.has(dropNumber)) dropNumber++;

            const newDropId = `new-drop-${Date.now()}`;
            const newDropName = getStandardDropName(dropNumber);
            setDrops([...drops, { id: newDropId, name: newDropName, drop_number: dropNumber, tasks: [] }].sort((a, b) => getDropNumber(a) - getDropNumber(b)));
            setActiveDropId(newDropId);
        }
    };
    const handleDropNameChange = (dropId, newLabel) => {
        setDrops(prevDrops => prevDrops.map(drop =>
            drop.id === dropId
                ? { ...drop, name: buildDropName(getDropPrefix(drop.name), newLabel) }
                : drop
        ));
    };

    const confirmRemoveDrop = () => { const dropId = removingDropId; setRemovingDropId(null); setDrops(prev => prev.filter(d => d.id !== dropId)); if (activeDropId === dropId) setActiveDropId(null); };
    const handleDragStart = (e, interfaceKey) => e.dataTransfer.setData("interfaceKey", interfaceKey);

    const handleDrop = (e, targetDropId) => {
        e.preventDefault();
        if (!targetDropId) return;
        const interfaceKey = e.dataTransfer.getData("interfaceKey");
        if (!interfaceKey) return;

        setExpandedInterfaces(prev => ({ ...prev, [interfaceKey]: true }));

        let movedTasks = [];
        const newDrops = drops.map(drop => {
            const tasksToKeep = drop.tasks.filter(task => {
                if (task.interface_name === interfaceKey) {
                    movedTasks.push(task);
                    return false;
                }
                return true;
            });
            return { ...drop, tasks: tasksToKeep };
        });

        if (movedTasks.length === 0) {
            movedTasks = DEFAULT_TASKS.map(taskName => ({
                id: `new-${interfaceKey}-${taskName}-${Date.now()}`,
                interface_name: interfaceKey,
                task_name: taskName,
                start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
            }));
        }

        const finalDrops = newDrops.map(drop =>
            drop.id === targetDropId ? {
                ...drop,
                tasks: [...drop.tasks, ...movedTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
            } : drop
        );
        setDrops(finalDrops);
    };

    const handleDragOver = (e) => e.preventDefault();
    const handleTaskChange = (dropId, taskId, field, value) => { setDrops(prevDrops => prevDrops.map(drop => { if (drop.id !== dropId) return drop; return { ...drop, tasks: drop.tasks.map(task => { if (task.id !== taskId) return task; if (field === 'responsible_user_ids') return { ...task, responsible_user_ids: value }; return { ...task, [field]: value }; }) }; })); };

    const handleDeleteCustomInterface = (interfaceKey, category) => {
        setDeletingCustomInterface({ interfaceKey, category });
    };

    const confirmDeleteCustomInterface = async () => {
        if (!deletingCustomInterface) return;
        const { interfaceKey, category } = deletingCustomInterface;
        const ifaceLabel = getInterfaceLabel(interfaceKey, flatProjectInterfaces);
        setDeletingCustomInterface(null);

        try {
            await axiosInstance.delete(`/api/projects/${originalProjectName}/custom-interfaces/${interfaceKey}`);

            setActiveInterfaces(prev => { const next = { ...prev }; delete next[interfaceKey]; return next; });
            setSelectedFields(prev => { const next = { ...prev }; delete next[interfaceKey]; return next; });
            setAllMasterFields(prev => { const next = { ...prev }; delete next[interfaceKey]; return next; });

            setDrops(prevDrops => prevDrops.map(drop => ({
                ...drop,
                tasks: drop.tasks.filter(t => t.interface_name !== interfaceKey)
            })).filter(drop => drop.tasks.length > 0 || drop.id.toString().startsWith('new-drop')));

            setProjectInterfaces(prev => {
                const updated = { ...prev };
                if (updated[category]) {
                    updated[category] = updated[category].filter(i => i.key !== interfaceKey);
                }
                return updated;
            })

            setToastInfo({ message: `Custom interface "${ifaceLabel}" deleted successfully.`, id: Date.now() });

        } catch (err) {
            if (err.response?.status === 401) return;
            setPageNotice({
                tone: 'error',
                title: 'Could Not Delete Interface',
                message: err.response?.data?.detail || "Failed to delete custom interface."
            });
        }
    };

    const handleCreateCustomInterface = async (payload) => {
        setIsCreatingCustomInterface(true);
        setError('');
        try {
            await axiosInstance.post(`/api/projects/${originalProjectName}/custom-interfaces`, payload);

            const newKey = payload.interface_name;
            const targetCategory = payload.interface_drop;

            setProjectInterfaces(prev => {
                const updated = { ...prev };
                if (!updated[targetCategory]) updated[targetCategory] = [];
                if (!updated[targetCategory].find(i => i.key === newKey)) {
                    updated[targetCategory] = [
                        ...updated[targetCategory],
                        { key: newKey, label: getInterfaceLabel(newKey, flatProjectInterfaces), isCustom: true }
                    ];
                }
                return updated;
            });

            setActiveInterfaces(prev => ({ ...prev, [newKey]: true }));
            setAllMasterFields(prev => ({ ...prev, [newKey]: [] }));
            setSelectedFields(prev => ({ ...prev, [newKey]: [] }));

            const newTasks = DEFAULT_TASKS.map(taskName => ({
                id: `custom-${newKey}-${taskName}-${Date.now()}`,
                interface_name: newKey,
                task_name: taskName,
                start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
            }));

            setDrops(prevDrops => {
                let updatedDrops = [...prevDrops];
                let targetDropIndex = updatedDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));

                if (targetDropIndex >= 0) {
                    const existingDrop = updatedDrops[targetDropIndex];
                    updatedDrops[targetDropIndex] = {
                        ...existingDrop,
                        tasks: [...existingDrop.tasks, ...newTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
                    };
                } else {
                    const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                    const dropNumber = dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : updatedDrops.length + 1;
                    updatedDrops.push({
                        id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        name: targetCategory,
                        drop_number: dropNumber,
                        tasks: newTasks
                    });
                    updatedDrops.sort((a, b) => a.drop_number - b.drop_number);
                }
                return updatedDrops;
            });

            setExpandedInterfaces(prev => ({ ...prev, [newKey]: true }));
            setToastInfo({ message: `Custom interface "${newKey}" created!`, id: Date.now() });

            setIsCustomInterfaceModalOpen(false);
        } catch (err) {
            if (err.response?.status === 401) return;
            setPageNotice({
                tone: 'error',
                title: 'Could Not Create Interface',
                message: err.response?.data?.detail || "Failed to create custom interface."
            });
        } finally {
            setIsCreatingCustomInterface(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccessMessage('');

        if (unassignedInterfaces.length > 0) {
            setError(`Cannot save. Please assign all ${unassignedInterfaces.length} active interface(s) to a drop before saving.`);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSaving(false);
            return;
        }

        try {
            const cancelKeys = Object.keys(pendingCancellations);
            if (cancelKeys.length > 0) {
                const cancelPromises = cancelKeys.map(ifaceKey =>
                    axiosInstance.post(`/api/projects/${originalProjectName.trim()}/interfaces/${ifaceKey}/cancel`, {
                        reason: pendingCancellations[ifaceKey]
                    })
                );
                await Promise.all(cancelPromises);
            }

            const uncancelKeys = Array.from(pendingUncancellations);
            if (uncancelKeys.length > 0) {
                const uncancelPromises = uncancelKeys.map(ifaceKey =>
                    axiosInstance.post(`/api/projects/${originalProjectName.trim()}/interfaces/${ifaceKey}/uncancel`)
                );
                await Promise.all(uncancelPromises);
            }

            const fieldsPayload = [];
            const allKnownFieldIds = new Set(Object.values(allMasterFields).flat().map(f => f.id));

            allKnownFieldIds.forEach(fieldId => {
                let belongsToActiveInterface = false;
                for (const ifaceKey in allMasterFields) {
                    if (activeInterfaces[ifaceKey] && allMasterFields[ifaceKey].some(f => f.id === fieldId)) {
                        belongsToActiveInterface = true;
                        break;
                    }
                }

                if (!belongsToActiveInterface) return;

                const isCurrentlySelected = Object.values(selectedFields).some(ids => ids.includes(fieldId));
                const overrideStatus = mandatoryOverrides[fieldId];
                const reason = omissionReasons[fieldId];

                let payloadOverride = overrideStatus === undefined ? null : overrideStatus;
                let payloadReason = (payloadOverride === false) ? reason : null;

                if (isCurrentlySelected || payloadOverride !== null || (payloadOverride === false && payloadReason !== null)) {
                    fieldsPayload.push({
                        field_id: fieldId,
                        is_selected: isCurrentlySelected,
                        is_mandatory_override: payloadOverride,
                        reason_for_omission: payloadReason
                    });
                }
            });

            const standardInterfacesPayload = {};
            Object.keys(activeInterfaces).forEach(key => {
                if (flatProjectInterfaces.some(i => i.key === key && !i.isCustom)) {
                    standardInterfacesPayload[key] = activeInterfaces[key];
                }
            });

            const projectDetailsPayload = {
                project_name: projectName.trim(),
                ...standardInterfacesPayload,
                selected_field_ids: fieldsPayload,
                // Hand any interfaces/fields created by an import in this session over to the
                // project. Until this runs they stay unclaimed and invisible to everyone else.
                imported_custom_field_ids: importedCustomFieldIds,
                imported_custom_interface_ids: importedCustomInterfaceIds
            };

            const schedulePayload = {
                drops: drops.map((drop, index) => ({
                    drop_number: index + 1,
                    name: drop.name,
                    tasks: drop.tasks.map(task => ({
                        interface_name: task.interface_name,
                        task_name: task.task_name,
                        start_date: task.start_date || null,
                        end_date: task.end_date || null,
                        status: task.status,
                        responsible_user_ids: task.responsible_user_ids || []
                    }))
                }))
            };

            const userUpdatePayload = {
                user_ids: assignedUsers.map(u => u.id)
            };

            await Promise.all([
                axiosInstance.put(`/api/projects/${projectId}`, projectDetailsPayload),
                axiosInstance.put(`/api/projects/${projectId}/schedule`, schedulePayload),
                axiosInstance.put(`/api/projects/${projectId}/users`, userUpdatePayload)
            ]);

            if (cancelKeys.length > 0) {
                setCancelledInterfaces(prev => {
                    const newSet = new Set(prev);
                    cancelKeys.forEach(k => newSet.add(k));
                    return newSet;
                });
                setPendingCancellations({});
            }

            setPendingUncancellations(new Set());

            // Clear the draft on successful save
            sessionStorage.removeItem(`wysupp_edit_project_draft_${projectId}`);
            setImportedCustomFieldIds([]);
            setImportedCustomInterfaceIds([]);

            setOriginalProjectName(projectName.trim());

            setSuccessMessage('Project, schedule, and users updated successfully!');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => setSuccessMessage(''), 5000);
            setSaving(false);
        } catch (err) {
            if (err.response?.status === 401) return;

            let errorMessage = "An unexpected error occurred. Please try again.";
            const errorDetail = err.response?.data?.detail;

            if (typeof errorDetail === 'string') {
                if (errorDetail.includes("already exists")) {
                    errorMessage = "A project with this name already exists. Please choose a different name.";
                } else {
                    errorMessage = errorDetail;
                }
            } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
                const firstError = errorDetail[0];
                errorMessage = `Validation Error: ${firstError.msg} (in ${firstError.loc.join(' > ')})`;
            } else {
                errorMessage = err.message || 'Unknown error';
            }

            setError(errorMessage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSaving(false);
        }
    };

    const handleOpenCancelModal = (interfaceKey) => { setCancellingInterface(interfaceKey); setIsCancelModalOpen(true); };

    const handleConfirmCancel = (reason) => {
        if (!cancellingInterface) return;

        setPendingCancellations(prev => ({ ...prev, [cancellingInterface]: reason }));

        setDrops(prevDrops => prevDrops.map(drop => ({
            ...drop,
            tasks: drop.tasks.filter(t => t.interface_name !== cancellingInterface)
        })).filter(drop => drop.tasks.length > 0 || drop.id.toString().startsWith('new-drop')));

        setSelectedFields(prev => { const newSelected = { ...prev }; delete newSelected[cancellingInterface]; return newSelected; });

        const fieldsForInterface = (allMasterFields[cancellingInterface] || []).map(f => f.id);
        setOmissionReasons(prev => { const newReasons = { ...prev }; fieldsForInterface.forEach(fieldId => { delete newReasons[fieldId]; }); return newReasons; });
        setMandatoryOverrides(prev => { const newOverrides = { ...prev }; fieldsForInterface.forEach(fieldId => { delete newOverrides[fieldId]; }); return newOverrides; });

        setIsCancelModalOpen(false);
        setCancellingInterface(null);
    };

    const handleUncancelInterface = (key) => {
        setPendingUncancellations(prev => { const newSet = new Set(prev); newSet.add(key); return newSet; });

        if (cancelledInterfaces.has(key)) {
            setCancelledInterfaces(prev => { const newSet = new Set(prev); newSet.delete(key); return newSet; });
        }
        if (pendingCancellations[key]) {
            setPendingCancellations(prev => { const newPending = { ...prev }; delete newPending[key]; return newPending; });
        }

        setActiveInterfaces(prev => ({ ...prev, [key]: true }));

        if (allMasterFields[key] && !selectedFields[key]) {
            const allFieldIdsForInterface = allMasterFields[key].map(field => field.id);
            const initialSelection = allFieldIdsForInterface.filter(id => {
                const field = allMasterFields[key].find(f => f.id === id);
                return field.is_mandatory && mandatoryOverrides[id] !== false;
            });
            setSelectedFields(prev => ({ ...prev, [key]: initialSelection }));
        }

        let targetCategory = null;
        for (const [categoryName, interfacesArray] of Object.entries(projectInterfaces)) {
            if (interfacesArray.some(int => int.key === key)) {
                targetCategory = categoryName;
                break;
            }
        }

        if (targetCategory) {
            setDrops(prevDrops => {
                let updatedDrops = [...prevDrops];
                let targetDropIndex = updatedDrops.findIndex(d => isSameDropSlot(d.name, targetCategory));

                const newTasks = DEFAULT_TASKS.map(taskName => ({
                    id: `new-${key}-${taskName}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                    interface_name: key,
                    task_name: taskName,
                    start_date: null, end_date: null, status: 'Pending', responsible_user_ids: []
                }));

                if (targetDropIndex >= 0) {
                    const existingDrop = updatedDrops[targetDropIndex];
                    if (!existingDrop.tasks.some(t => t.interface_name === key)) {
                        updatedDrops[targetDropIndex] = {
                            ...existingDrop,
                            tasks: [...existingDrop.tasks, ...newTasks].sort((a, b) => DEFAULT_TASKS.indexOf(a.task_name) - DEFAULT_TASKS.indexOf(b.task_name))
                        };
                    }
                } else {
                    const dropIndexInDefaults = DROP_NAMES.indexOf(targetCategory);
                    const dropNumber = dropIndexInDefaults >= 0 ? dropIndexInDefaults + 1 : updatedDrops.length + 1;
                    updatedDrops.push({
                        id: `auto-drop-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        name: targetCategory,
                        drop_number: dropNumber,
                        tasks: newTasks
                    });
                    updatedDrops.sort((a, b) => a.drop_number - b.drop_number);
                }
                return updatedDrops;
            });
            setExpandedInterfaces(prev => ({ ...prev, [key]: true }));
        }

        const ifaceLabel = getInterfaceLabel(key, flatProjectInterfaces);
        setToastInfo({ message: `"${ifaceLabel}" uncancelled and restored!`, id: Date.now() });
    };

    const handleOpenAuthModal = () => { setAuthError(''); setIsAuthModalOpen(true); };
    // The password goes with the deletion itself and is checked there. It used
    // to be checked by a separate call first, which the delete endpoint simply
    // trusted - so calling the delete directly skipped the password altogether.
    const handleConfirmDeleteWithAuth = async (password) => {
        setError('');
        setSuccessMessage('');
        setAuthError('');
        setIsDeleting(true);
        try {
            await axiosInstance.post(`/api/projects/${projectId}/delete`, { password });
            sessionStorage.removeItem(`wysupp_edit_project_draft_${projectId}`);
            setIsAuthModalOpen(false);
            setPageNotice({
                tone: 'success',
                title: 'Project Deleted',
                message: <>Project <strong style={{ color: 'var(--text-color)' }}>{originalProjectName}</strong> was successfully deleted.</>,
                onAcknowledge: () => navigate('/admin/manage-projects')
            });
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail;
            // A 401 here means the session ended; the axios interceptor handles it.
            if (status === 401) return;

            if (status === 403 && detail === "Incorrect password.") {
                // A wrong password answers 403, not 401, so the interceptor does
                // not mistake it for an expired session. The modal stays open.
                setAuthError('Incorrect password. Please try again.');
            } else if (status === 429) {
                setAuthError(typeof detail === 'string' ? detail : 'Too many failed attempts. Try again in a few minutes.');
            } else {
                setError(typeof detail === 'string' ? detail : 'Failed to delete project.');
                setIsAuthModalOpen(false);
            }
            setIsDeleting(false);
        }
    };

    if (accessDenied) return (
        <div className="admin-page-container">
            <div className="loading-state">
                <FaExclamationTriangle style={{ fontSize: '2.5rem', color: '#fca5a5', marginBottom: '1rem' }} />
                <h3 style={{ margin: '0 0 0.5rem 0' }}>Access Denied</h3>
                <p style={{ marginTop: 0 }}>You do not have permission to access this project.</p>
                <button
                    type="button"
                    onClick={() => navigate('/admin/manage-projects')}
                    style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', border: 'none', borderRadius: '6px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#48bb78', color: '#1a202c' }}
                >
                    Back to Projects
                </button>
            </div>
            <style>{`:root { --dark-bg: #1a202c; --text-color: #e2e8f0; } .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; display: flex; align-items: center; justify-content: center; font-family: 'Inter', sans-serif; } .loading-state { text-align: center; color: var(--text-color); }`}</style>
        </div>
    );

    if (loading) return (<div className="admin-page-container"><div className="loading-state"><div className="loading-spinner"></div><p>Loading project data...</p></div> <style>{`:root { --dark-bg: #1a202c; --text-color: #e2e8f0; --green-accent: #48bb78; --border-color: #4a5568; } .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; display: flex; align-items: center; justify-content: center; } .loading-state { text-align: center; color: var(--text-color); } .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style> </div>);

    return (
        <div className="admin-page-container">
            {toastInfo.message && (
                <div key={toastInfo.id} className="toast-notification">
                    <FaCheckCircle size={18} />
                    {toastInfo.message}
                </div>
            )}
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/admin/manage-projects')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="page-title">Edit Project</h1>
                </div>

                <div className="form-wrapper">
                    {error && <div className="form-feedback error">{error}</div>}
                    {successMessage && <div className="form-feedback success">{successMessage}</div>}

                    <form onSubmit={handleSubmit} className="project-form">
                        {/* --- Section 1 --- */}
                        <div className="form-section">
                            <div className="form-section-header-row">
                                <h2 className="form-section-title no-border">1. Project Details</h2>
                                <button type="button" onClick={handleDiscardChanges} className="clear-project-btn" title="Discard all unsaved changes and revert to original data">
                                    <FaUndo /> Reset Changes
                                </button>
                            </div>
                            <div className="input-group">
                                <label htmlFor="project-name" className="input-label">Project Name</label>
                                <input id="project-name" type="text" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
                            </div>
                        </div>

                        {/* --- Section 2 --- */}
                        <div className="form-section">
                            <div className="form-section-header-row">
                                <h2 className="form-section-title" style={{ borderBottom: 'none', marginBottom: 0 }}>2. Active Interfaces & Fields</h2>
                                <div>
                                    <input
                                        type="file"
                                        accept=".xlsx"
                                        style={{ display: 'none' }}
                                        onChange={handleImportExcel}
                                        ref={excelInputRef}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowImportWarningModal(true)}
                                        className="action-button secondary"
                                        disabled={isParsingExcel}
                                        title="Upload a customized RELEX Interface Document to auto-select interfaces and fields"
                                    >
                                        {isParsingExcel ? <FaSpinner className="button-spinner" style={{ marginRight: '8px' }} /> : <FaFileExcel style={{ marginRight: '8px' }} />}
                                        {isParsingExcel ? 'Parsing Document...' : 'Import from Interfaces Document'}
                                    </button>
                                </div>
                            </div>
                            <div className="category-layout">
                                <div className="category-sidebar">
                                    {Object.keys(projectInterfaces).map(category => {
                                        const interfacesInDrop = projectInterfaces[category] || [];
                                        const totalInDrop = interfacesInDrop.length;
                                        const selectedInDrop = interfacesInDrop.filter(int => activeInterfaces[int.key]).length;

                                        return (
                                            <button key={category} type="button" className={`category-button ${activeCategory === category ? 'active' : ''}`} onClick={() => { setActiveCategory(category); setInterfaceSearchTerm(''); }}>
                                                <span className="category-button-label">{category}</span>
                                                <span className="category-button-counter">({selectedInDrop} / {totalInDrop})</span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="category-content" ref={categoryContentRef}>
                                    {projectInterfaces[activeCategory] && (
                                        <>
                                            <div className="interface-search-container">
                                                <input
                                                    type="text"
                                                    placeholder={`Search ${activeCategory} interfaces...`}
                                                    value={interfaceSearchTerm}
                                                    onChange={(e) => setInterfaceSearchTerm(e.target.value)}
                                                    className="interface-search-input"
                                                />
                                                <div className="module-filters">
                                                    <span style={{ fontSize: '0.85rem', color: 'var(--light-text)', marginRight: '0.5rem' }}>Filter by Module:</span>
                                                    {['F&R', 'S&A', 'P&P'].map(mod => (
                                                        <button
                                                            key={mod}
                                                            type="button"
                                                            className={`module-filter-btn ${moduleFilters[mod] ? 'active' : ''}`}
                                                            onClick={() => toggleModuleFilter(mod)}
                                                        >
                                                            {mod}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="checkbox-grid">
                                                {projectInterfaces[activeCategory]
                                                    .filter(int => {
                                                        if (!int.label.toLowerCase().includes(interfaceSearchTerm.toLowerCase())) return false;
                                                        if (activeModuleList.length > 0) {
                                                            if (!int.modules || int.modules.length === 0) return false;
                                                            return int.modules.some(mod => activeModuleList.includes(mod));
                                                        }
                                                        return true;
                                                    })
                                                    .sort((a, b) => (delayedSortState[b.key] || false) - (delayedSortState[a.key] || false))
                                                    .map(int => {
                                                        const currentSelectedIds = selectedFields[int.key] || [];
                                                        const totalFields = allMasterFields[int.key]?.length || 0;
                                                        const isCurrentlyActive = activeInterfaces[int.key];
                                                        const isCancelled = cancelledInterfaces.has(int.key) || !!pendingCancellations[int.key];

                                                        // --- NEW: Check for incomplete custom fields ---
                                                        const hasIncompleteCustomFields = (allMasterFields[int.key] || []).some(f => isCustomFieldIncomplete(f));

                                                        return (
                                                            <div
                                                                key={int.key}
                                                                className={`checkbox-item-wrapper ${isCancelled ? 'item-cancelled' : ''} ${activeInterfaces[int.key] ? 'is-selected' : ''}`}
                                                                style={{ position: 'relative' }}
                                                            >
                                                                {/* --- NEW: Interface Level Warning --- */}
                                                                {/* --- NEW: Interface Level Warning --- */}
                                                                {hasIncompleteCustomFields && (
                                                                    <div
                                                                        className="card-deadline-warning"
                                                                        title="Missing values on custom fields"
                                                                    >
                                                                        <FaExclamationTriangle />
                                                                    </div>
                                                                )}

                                                                <Checkbox
                                                                    id={`edit-${int.key}`}
                                                                    name={int.key}
                                                                    checked={isCurrentlyActive || false}
                                                                    onChange={() => handleInterfaceCheckboxChange(int.key)}
                                                                    label={int.label}
                                                                    disabled={isCancelled}
                                                                />

                                                                <div className="interface-module-tags">
                                                                    {int.modules && int.modules.map(mod => (
                                                                        <span key={mod} className={`module-tag tag-${mod.replace('&', '')}`}>{mod}</span>
                                                                    ))}
                                                                </div>

                                                                {isCancelled ? (
                                                                    <div className="interface-actions-compact visible">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleUncancelInterface(int.key)}
                                                                            className="uncancel-interface-button"
                                                                            title="Restore this interface to the project"
                                                                        >
                                                                            <FaUndo /> Uncancel Interface
                                                                        </button>
                                                                    </div>
                                                                ) : isCurrentlyActive ? (
                                                                    <div className={`interface-actions-compact ${activeInterfaces[int.key] ? 'visible' : 'hidden'}`}>
                                                                        {(allMasterFields[int.key] || int.isCustom) && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleOpenModal(int.key)}
                                                                                className="field-selector-button"
                                                                            >
                                                                                Fields ({currentSelectedIds.length} / {totalFields})
                                                                            </button>
                                                                        )}

                                                                        {int.isCustom ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleDeleteCustomInterface(int.key, activeCategory)}
                                                                                className="delete-custom-interface-button"
                                                                            >
                                                                                <FaTrash /> Delete Interface
                                                                            </button>
                                                                        ) : (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleOpenCancelModal(int.key)}
                                                                                className="cancel-interface-button"
                                                                            >
                                                                                Cancel Interface
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        );
                                                    })}

                                                <div className="custom-interface-card" onClick={() => setIsCustomInterfaceModalOpen(true)}>
                                                    <div className="custom-interface-card-content">
                                                        <span className="plus-symbol"><FaPlus /></span>
                                                        <span>Create Custom Interface for {activeCategory.split(' - ')[0]}</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {projectInterfaces[activeCategory].filter(int => {
                                                if (!int.label.toLowerCase().includes(interfaceSearchTerm.toLowerCase())) return false;
                                                if (activeModuleList.length > 0) {
                                                    if (!int.modules || int.modules.length === 0) return false;
                                                    return int.modules.some(mod => activeModuleList.includes(mod));
                                                }
                                                return true;
                                            }).length === 0 && (
                                                    <div className="no-users-message" style={{ marginTop: '1.5rem' }}>
                                                        No interfaces match your search and module filters.
                                                    </div>
                                                )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* --- Section 3 --- */}
                        <div className="form-section">
                            <h2 className="form-section-title">3. User Management</h2>
                            <div className="user-management-grid">
                                <div className="assigned-users-list">
                                    <h4>Assigned Users</h4>
                                    {assignedUsers.length > 0 ? (
                                        <ul>
                                            {assignedUsers.map(user => (
                                                <li key={user.id}>
                                                    <span>{user.username}</span>
                                                    <button type="button" onClick={() => handleRemoveUser(user.id)}>&times;</button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="no-users-message">No users are assigned to this project yet.</p>
                                    )}
                                </div>
                                <div className="add-user-form">
                                    <h4>Add User to Project</h4>
                                    <div className="autocomplete-wrapper">
                                        <input type="text" placeholder="Search by username or email..." value={userSearchTerm} onChange={(e) => setUserSearchTerm(e.target.value)} />
                                        {userSearchResults.length > 0 && (
                                            <div className="autocomplete-results">
                                                {userSearchResults.map(user => (
                                                    <div key={user.id} className="autocomplete-item" onClick={() => handleAddUser(user)}>
                                                        <strong>{user.username}</strong> <span>({user.email})</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {isSearching && <div className="autocomplete-results"><div className="autocomplete-item">Searching...</div></div>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- Section 4 --- */}
                        <div className="form-section">
                            <div className="schedule-section-header">
                                <h2 className="form-section-title">4. Schedule Organization & Dates</h2>
                            </div>
                            <div className="schedule-editor">
                                <div className="unassigned-pool">
                                    <h4>Available to Schedule</h4>
                                    {unassignedInterfaces.length > 0 && (
                                        <p className="unassigned-warning-message">
                                            All interfaces must be assigned to a drop before saving.
                                        </p>
                                    )}
                                    <div className="interface-list" onDrop={(e) => handleDrop(e, activeDropId)} onDragOver={handleDragOver}>
                                        {unassignedInterfaces.map(int => (
                                            <div key={int.key} draggable onDragStart={(e) => handleDragStart(e, int.key)} className="draggable-item">{int.label}</div>
                                        ))}
                                    </div>
                                </div>
                                <div className="schedule-content-wrapper">
                                    <div className="drop-tabs-container">
                                        {drops.map(drop => (
                                            <button key={drop.id} type="button" title={drop.name} className={`drop-tab ${activeDropId === drop.id ? 'active' : ''}`} onClick={() => setActiveDropId(drop.id)}>
                                                {drop.name.split(' - ')[0]}
                                            </button>
                                        ))}
                                        {drops.length < dropLimit && <button type="button" onClick={handleAddDrop} className="add-drop-tab-button">+ Add Drop</button>}
                                    </div>

                                    <div className="active-drop-area" onDrop={(e) => handleDrop(e, activeDropId)} onDragOver={handleDragOver}>
                                        {!activeDrop ? (
                                            <p className="drop-placeholder">Please add or select a drop to begin scheduling.</p>
                                        ) : (
                                            <>
                                                <div className="drop-header">
                                                    <div className="drop-name-editor">
                                                        {getDropPrefix(activeDrop.name) && (
                                                            <span className="drop-name-prefix">{getDropPrefix(activeDrop.name)} -</span>
                                                        )}
                                                        <input
                                                            type="text"
                                                            value={getDropLabel(activeDrop.name)}
                                                            onChange={(e) => handleDropNameChange(activeDrop.id, e.target.value)}
                                                            className="drop-name-input"
                                                            placeholder="Name this drop"
                                                            title="Rename this drop"
                                                            aria-label="Drop name"
                                                            maxLength={80}
                                                        />
                                                    </div>
                                                    <div className="drop-header-actions">
                                                        <button
                                                            type="button"
                                                            className="copy-dates-button paste"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePasteDatesToDrop(activeDrop.id);
                                                            }}
                                                            title={`Paste copied dates & users to all interfaces in ${activeDrop.name}`}
                                                            disabled={!dateClipboard || activeDrop.tasks.length === 0}
                                                        >
                                                            <FaPaste /> Paste to Entire Drop
                                                        </button>
                                                        <button type="button" onClick={() => setRemovingDropId(activeDrop.id)} className="remove-drop-button">&times;</button>
                                                    </div>
                                                </div>
                                                <div className="task-table-container">
                                                    {Object.keys(groupedTasks).length > 0 ? (
                                                        <table className="task-table">
                                                            <thead><tr><th>Task</th><th>Start</th><th>End</th><th>Responsible</th></tr></thead>
                                                            <tbody>
                                                                {Object.entries(groupedTasks).map(([interfaceName, tasks]) => (
                                                                    <React.Fragment key={interfaceName}>
                                                                        <tr className="interface-group-header" >
                                                                            <td colSpan="4">
                                                                                <div className="interface-header-cell">
                                                                                    <div className="interface-header-label" onClick={() => toggleInterfaceExpansion(interfaceName)}>
                                                                                        <span>{getInterfaceLabel(interfaceName, flatProjectInterfaces)}</span>
                                                                                        <span className="toggle-icon">
                                                                                            {expandedInterfaces[interfaceName] ? <FaChevronUp /> : <FaChevronDown />}
                                                                                        </span>
                                                                                    </div>
                                                                                    <div className="interface-header-actions">
                                                                                        <button
                                                                                            type="button"
                                                                                            className="copy-dates-button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleCopyDates(activeDrop.id, interfaceName);
                                                                                            }}
                                                                                            title={`Copy dates & users from ${getInterfaceLabel(interfaceName, flatProjectInterfaces)}`}
                                                                                        >
                                                                                            <FaCopy /> Copy Dates & Users
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="copy-dates-button paste"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handlePasteDates(activeDrop.id, interfaceName);
                                                                                            }}
                                                                                            title={`Paste copied dates & users to ${getInterfaceLabel(interfaceName, flatProjectInterfaces)}`}
                                                                                            disabled={!dateClipboard}
                                                                                        >
                                                                                            <FaPaste /> Paste Dates & Users
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className="unschedule-button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleUnscheduleInterface(activeDrop.id, interfaceName);
                                                                                            }}
                                                                                            title={`Remove "${getInterfaceLabel(interfaceName, flatProjectInterfaces)}" from schedule`}
                                                                                        >
                                                                                            <FaTimes />
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                        {expandedInterfaces[interfaceName] && tasks.map(task => (
                                                                            <tr key={task.id} className="task-row">
                                                                                <td className="task-name-cell">{task.task_name}</td>
                                                                                <td><input type="date" value={task.start_date || ''} onChange={e => handleTaskChange(activeDrop.id, task.id, 'start_date', e.target.value)} /></td>
                                                                                <td><input type="date" value={task.end_date || ''} onChange={e => handleTaskChange(activeDrop.id, task.id, 'end_date', e.target.value)} /></td>
                                                                                <td><MultiUserSelect allUsers={projectUsers} selectedUserIds={task.responsible_user_ids || []} onChange={(newSelectedIds) => handleTaskChange(activeDrop.id, task.id, 'responsible_user_ids', newSelectedIds)} /></td>
                                                                            </tr>
                                                                        ))}
                                                                    </React.Fragment>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    ) : <p className="drop-placeholder">Drop interfaces from the "Available to Schedule" list here.</p>}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* --- Form Actions --- */}
                        <div className="form-actions">
                            <button type="button" className="action-button secondary" onClick={() => navigate('/admin/manage-projects')}>Cancel</button>
                            <button
                                type="submit"
                                disabled={saving || isDeleting || unassignedInterfaces.length > 0}
                                className="action-button primary"
                                title={unassignedInterfaces.length > 0 ? `Cannot save: ${unassignedInterfaces.length} interface(s) are still in 'Available to Schedule'` : 'Save all changes'}
                            >
                                {saving ? 'Saving...' : 'Save All Changes'}
                            </button>
                        </div>
                    </form>

                    {/* --- Danger Zone --- */}
                    <div className="danger-zone">
                        <h2 className="danger-zone-title"><FaExclamationTriangle /> Danger Zone</h2>
                        <div className="danger-zone-actions">
                            <div className="danger-action">
                                <p><strong>Delete Project</strong><br />Permanently remove this project and all associated data (interfaces, schedule, history). This action requires password confirmation.</p>
                                <button
                                    onClick={handleOpenAuthModal}
                                    className="action-button delete-button"
                                    disabled={saving || isDeleting}
                                >
                                    <FaTrash /> Delete Project
                                </button>
                            </div>
                        </div>
                    </div>

                </div>

                {/* --- Modals --- */}
                {isModalOpen && editingInterface && (
                    <FieldSelectorModal
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        interfaceName={editingInterface}
                        fields={groupedMasterFields[editingInterface] || {}}
                        selectedFieldIds={selectedFields[editingInterface] || []}
                        omissionReasons={omissionReasons}
                        mandatoryOverrides={mandatoryOverrides}
                        onSelectionChange={handleFieldSelectionChange}
                        onReasonChange={handleReasonChange}
                        onMandatoryOverrideChange={handleMandatoryOverrideChange}
                        onFieldAdded={handleFieldAdded}
                        onFieldUpdated={handleFieldUpdated}
                        onFieldDeleted={handleFieldDeleted}
                        flatInterfaces={flatProjectInterfaces}
                    />
                )}

                <CancelInterfaceModal
                    isOpen={isCancelModalOpen}
                    onClose={() => setIsCancelModalOpen(false)}
                    onConfirm={handleConfirmCancel}
                    interfaceName={cancellingInterface || ''}
                    flatInterfaces={flatProjectInterfaces}
                />

                <ReAuthModal
                    isOpen={isAuthModalOpen}
                    onClose={() => setIsAuthModalOpen(false)}
                    onConfirm={handleConfirmDeleteWithAuth}
                    isLoading={isDeleting}
                    error={authError}
                    projectName={projectName}
                />

                <CreateCustomInterfaceModal
                    isOpen={isCustomInterfaceModalOpen}
                    onClose={() => setIsCustomInterfaceModalOpen(false)}
                    onSubmit={handleCreateCustomInterface}
                    dropName={activeCategory}
                />

                {/* --- Import Pre-Flight Warning Modal --- */}
                {showImportWarningModal && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowImportWarningModal(false)}>
                        <div className="modal-content" style={{ maxWidth: '460px', padding: '2rem' }} onClick={e => e.stopPropagation()}>
                            <center>
                                <FaFileExcel style={{ fontSize: '3rem', color: '#48bb78', marginBottom: '1rem' }} />
                            </center>
                            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)', textAlign: 'center' }}>Before You Import</h3>
                            <div style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                <p style={{ marginBottom: '1rem' }}>
                                    Please make sure your Interface Document is set up correctly before importing:
                                </p>
                                <ul style={{ paddingLeft: '1.25rem', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <li>Each <strong>custom interface</strong> must have its own <strong>dedicated tab</strong> in the Excel file — interfaces without a matching tab will not have their fields detected.</li>
                                    <li><strong>This will replace the current interface selection entirely.</strong> Any interface not present in the document will be deactivated and removed from the schedule.</li>
                                    <li><strong>Cancelled interfaces are left as they are</strong> and won't be scheduled. Uncancel them in section 2 first if the document should import them.</li>
                                    <li>You can undo this at any time using the <strong>"Reset Changes"</strong> button.</li>
                                </ul>
                            </div>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    type="button"
                                    className="action-button secondary"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={() => setShowImportWarningModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="action-button primary"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={() => { setShowImportWarningModal(false); excelInputRef.current && excelInputRef.current.click(); }}
                                >
                                    <FaFileExcel style={{ marginRight: '8px' }} /> Continue & Select File
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Post-Import Warning Modal --- */}
                {(incompleteFieldsWarning > 0 || emptyInterfacesWarning > 0) && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }}>
                        <div className="modal-content" style={{ maxWidth: '450px', textAlign: 'center', padding: '2rem' }}>
                            <center>
                                <FaExclamationTriangle style={{ fontSize: '3rem', color: '#fcd34d', marginBottom: '1rem' }} />
                            </center>
                            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Review Required After Import</h3>
                            <div style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem', textAlign: 'left' }}>
                                {incompleteFieldsWarning > 0 && (
                                    <p style={{ marginBottom: emptyInterfacesWarning > 0 ? '1rem' : 0 }}>
                                        <strong>{incompleteFieldsWarning}</strong> custom field(s) were created but are missing some documentation (Table in RELEX, Description, Example Value, etc.).
                                    </p>
                                )}
                                {emptyInterfacesWarning > 0 && (
                                    <p style={{ marginBottom: 0 }}>
                                        <strong>{emptyInterfacesWarning}</strong> custom interface(s) were created but have <strong>no fields associated</strong> with them — their sheet may not have matched the standard format, or all fields were excluded.
                                    </p>
                                )}
                                <p style={{ marginTop: '1rem', marginBottom: 0 }}>
                                    Please review and complete these items using the field selector before saving.
                                </p>
                            </div>
                            <button
                                className="action-button primary"
                                style={{ width: '100%', justifyContent: 'center' }}
                                onClick={() => { setIncompleteFieldsWarning(0); setEmptyInterfacesWarning(0); }}
                            >
                                Understood
                            </button>
                        </div>
                    </div>
                )}

                {/* --- Discard Changes Confirmation Modal --- */}
                {showDiscardModal && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowDiscardModal(false)}>
                        <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '2rem' }} onClick={e => e.stopPropagation()}>
                            <center>
                                <FaExclamationTriangle style={{ fontSize: '3rem', color: '#fcd34d', marginBottom: '1rem' }} />
                            </center>
                            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Reset All Changes?</h3>
                            <p style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                This will discard all unsaved changes and revert the project to its last saved state.
                                {(importedCustomFieldIds.length > 0 || importedCustomInterfaceIds.length > 0) && (
                                    <><br /><br /><strong style={{ color: '#fca5a5' }}>Any interfaces and fields imported via the Interface Document will also be permanently deleted.</strong></>
                                )}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    type="button"
                                    className="action-button secondary"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={() => setShowDiscardModal(false)}
                                >
                                    Keep Editing
                                </button>
                                <button
                                    type="button"
                                    className="action-button danger"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={confirmDiscardChanges}
                                >
                                    Reset Changes
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Unschedule Interface Confirmation Modal --- */}
                {unschedulingInterface && (
                    <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setUnschedulingInterface(null)}>
                        <div className="modal-content" style={{ maxWidth: '420px', textAlign: 'center', padding: '2rem' }} onClick={e => e.stopPropagation()}>
                            <center>
                                <FaExclamationTriangle style={{ fontSize: '3rem', color: '#fcd34d', marginBottom: '1rem' }} />
                            </center>
                            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Remove From This Drop?</h3>
                            <p style={{ color: 'var(--light-text)', marginBottom: '1.5rem', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                <strong style={{ color: 'var(--text-color)' }}>{getInterfaceLabel(unschedulingInterface.interfaceName, flatProjectInterfaces)}</strong> will be moved back to the <strong>"Available to Schedule"</strong> list.
                                <br /><br />
                                The dates and responsible users set for it in this drop will be cleared.
                            </p>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <button
                                    type="button"
                                    className="action-button secondary"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={() => setUnschedulingInterface(null)}
                                >
                                    Keep Scheduled
                                </button>
                                <button
                                    type="button"
                                    className="action-button danger"
                                    style={{ flex: 1, justifyContent: 'center' }}
                                    onClick={confirmUnscheduleInterface}
                                >
                                    Remove From Drop
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ConfirmModal
                    isOpen={pastingToDropId !== null}
                    tone="warning"
                    confirmVariant="primary"
                    title="Paste To Entire Drop?"
                    message={<>
                        The copied dates and responsible users will be applied to all <strong style={{ color: 'var(--text-color)' }}>{new Set((drops.find(d => d.id === pastingToDropId)?.tasks || []).map(t => t.interface_name)).size}</strong> interfaces in this drop.
                        <br /><br />
                        Any dates already set on those interfaces will be overwritten.
                    </>}
                    confirmLabel="Paste to All"
                    cancelLabel="Cancel"
                    onConfirm={confirmPasteDatesToDrop}
                    onClose={() => setPastingToDropId(null)}
                />

                <ConfirmModal
                    isOpen={removingDropId !== null}
                    tone="danger"
                    title="Remove This Drop?"
                    message={<>
                        <strong style={{ color: 'var(--text-color)' }}>{drops.find(d => d.id === removingDropId)?.name}</strong> and all of its scheduled interfaces will be removed from this project.
                        <br /><br />
                        Those interfaces stay active and will return to the "Available to Schedule" list.
                    </>}
                    confirmLabel="Remove Drop"
                    cancelLabel="Keep Drop"
                    onConfirm={confirmRemoveDrop}
                    onClose={() => setRemovingDropId(null)}
                />

                <ConfirmModal
                    isOpen={!!deletingCustomInterface}
                    tone="danger"
                    title="Permanently Delete Interface?"
                    message={<>
                        <strong style={{ color: 'var(--text-color)' }}>{deletingCustomInterface && getInterfaceLabel(deletingCustomInterface.interfaceKey, flatProjectInterfaces)}</strong> will be permanently deleted, along with all of its fields, tasks and data.
                        <br /><br />
                        <strong style={{ color: '#fca5a5' }}>This happens immediately and cannot be undone — not even with "Reset Changes".</strong>
                    </>}
                    confirmLabel="Delete Interface"
                    cancelLabel="Keep Interface"
                    onConfirm={confirmDeleteCustomInterface}
                    onClose={() => setDeletingCustomInterface(null)}
                />

                <NoticeModal
                    isOpen={!!pageNotice}
                    tone={pageNotice?.tone}
                    title={pageNotice?.title}
                    message={pageNotice?.message}
                    onClose={() => {
                        const acknowledge = pageNotice?.onAcknowledge;
                        setPageNotice(null);
                        if (acknowledge) acknowledge();
                    }}
                />

            </main>
            <style>{`
                /* --- Root Variables --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --optional-override-color: #fcd34d; --optional-override-bg: rgba(245, 158, 11, 0.1); /* Yellowish */
                }
                /* --- Base & Layout --- */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .dashboard-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 3rem; background-color: var(--card-bg); border-bottom: 1px solid var(--border-color); }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); }
                .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
                .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
                .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .back-button:hover { background: #4a5568; }
                .page-title { font-size: 2rem; font-weight: 700; color: var(--text-color); }

                .form-wrapper { background-color: var(--card-bg); border-radius: 12px; padding: 2rem; border: 1px solid var(--border-color); }
                .project-form { display: flex; flex-direction: column; gap: 2.5rem; }
                .form-section { border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; background-color: #1a202c; }
                
                /* --- NEW HEADER LAYOUT FOR CLEAR BUTTON --- */
                .form-section-header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid var(--border-color);
                }
                .form-section-title { font-size: 1.25rem; font-weight: 600; margin: 0 0 1.5rem 0; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .form-section-title.no-border {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .clear-project-btn {
                    background-color: transparent;
                    border: 1px solid var(--danger-color);
                    color: var(--danger-color);
                    padding: 0.4rem 0.8rem;
                    border-radius: 6px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }
                .clear-project-btn:hover {
                    background-color: var(--error-bg);
                }

                .input-group { display: flex; flex-direction: column; }
                .input-label { font-size: 0.9rem; font-weight: 500; color: var(--light-text); margin-bottom: 0.5rem; }
                input:not([type="checkbox"]), select, textarea { width: 100%; padding: 0.75rem 1rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; font-size: 1rem; color: var(--text-color); transition: border-color 0.2s; box-sizing: border-box; }
                input:not([type="checkbox"]):focus, select:focus, textarea:focus { outline: none; border-color: var(--green-accent); }

                .category-layout { display: flex; gap: 1.5rem; border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--input-bg); }
                .category-sidebar { flex: 0 0 250px; padding: 1rem; border-right: 1px solid var(--border-color); }
                .category-button { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0.75rem 1rem; margin-bottom: 0.5rem; border: none; border-radius: 6px; background-color: transparent; cursor: pointer; text-align: left; font-size: 0.95rem; font-weight: 500; color: var(--light-text); transition: background-color 0.2s, color 0.2s; }
                .category-button:hover { background-color: var(--card-bg); }
                .category-button.active { background-color: var(--green-accent); color: var(--dark-bg); font-weight: 600; }
                .category-button-label { flex-grow: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-right: 0.5rem; }
                .category-button-counter { font-size: 0.8rem; font-weight: 400; color: var(--light-text); flex-shrink: 0; }
                .category-button.active .category-button-counter { color: var(--dark-bg); opacity: 0.8; font-weight: 500; }
                .category-content { flex-grow: 1; padding: 1.5rem; max-height: 400px; overflow-y: auto; overflow-anchor: none; }
                
                /* --- Interface Search Bar & Module Filters Styles --- */
                .interface-search-container { 
                    margin-bottom: 1.5rem; 
                    position: sticky; 
                    top: -1.5rem; 
                    z-index: 10; 
                    background-color: var(--input-bg); 
                    padding: 1rem 0 0.5rem 0; 
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }
                .interface-search-input { 
                    width: 100%; 
                    padding: 0.85rem 1.2rem; 
                    background-color: var(--card-bg); 
                    border: 1px solid var(--border-color); 
                    border-radius: 8px; 
                    color: var(--text-color); 
                    font-size: 0.95rem; 
                    transition: all 0.2s ease; 
                }
                .interface-search-input:focus { 
                    outline: none; 
                    border-color: var(--green-accent); 
                    box-shadow: 0 0 0 1px var(--green-accent); 
                }

                .module-filters {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .module-filter-btn {
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    color: var(--text-color);
                    padding: 0.25rem 0.6rem;
                    border-radius: 999px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .module-filter-btn:hover {
                    background-color: #4a5568;
                }
                .module-filter-btn.active {
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                    border-color: var(--green-accent);
                }

                .checkbox-grid { display: grid; overflow-x: hidden; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; }

                .checkbox-item-wrapper { background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; display: flex; flex-direction: column; justify-content: space-between; min-height: 100px; transition: all 0.2s ease-in-out; margin-top: 0.65rem; margin-right: 0.65rem;}
                .checkbox-item-wrapper.item-cancelled { background-color: var(--input-bg); opacity: 0.7; }
                .checkbox-item-wrapper.item-cancelled .checkbox-label { text-decoration: line-through; }
                .checkbox-container { flex-grow: 1; }
                .checkbox-wrapper { display: flex; align-items: center; }
                .checkbox-label { font-weight: 500; color: var(--text-color); }
                .checkbox-label.disabled { cursor: not-allowed; }

                /* --- Module Tags Styles --- */
                .interface-module-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.4rem;
                    margin-top: 0.5rem;
                    margin-left: 1.8rem;
                }
                .module-tag {
                    font-size: 0.65rem;
                    font-weight: 700;
                    padding: 0.15rem 0.4rem;
                    border-radius: 4px;
                    text-transform: uppercase;
                }
                .tag-FR { background-color: var(--success-bg); color: var(--green-accent); border: 1px solid rgba(72, 187, 120, 0.3); }
                .tag-SA { background-color: var(--optional-override-bg); color: var(--optional-override-color); border: 1px solid rgba(245, 158, 11, 0.3); }
                .tag-PP { background-color: var(--blue-bg); color: rgba(59, 131, 246, 0.69); border: 1px solid rgba(59, 130, 246, 0.3); }


                .checkbox-input, .field-item input[type="checkbox"], .add-field-footer input[type="checkbox"] { -webkit-appearance: none; appearance: none; flex-shrink: 0; width: 18px; height: 18px; border: 2px solid var(--border-color); border-radius: 4px; background-color: var(--input-bg); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.2s; }
                .checkbox-input { margin-right: 0.75rem; }
                .add-field-footer input[type="checkbox"] { margin-right: 0.5rem; }
                .checkbox-input:hover:not(:disabled), .field-item input[type="checkbox"]:hover:not(:disabled), .add-field-footer input[type="checkbox"]:hover:not(:disabled) { border-color: var(--light-text); }
                .checkbox-input:checked, .field-item input[type="checkbox"]:checked, .add-field-footer input[type="checkbox"]:checked { border-color: var(--green-accent); }
                .checkbox-input::before, .field-item input[type="checkbox"]::before, .add-field-footer input[type="checkbox"]::before { content: ""; width: 10px; height: 10px; transform: scale(0); transition: transform 0.1s ease-in-out; box-shadow: inset 1em 1em var(--green-accent); border-radius: 2px; }
                .checkbox-input:checked::before, .field-item input[type="checkbox"]:checked::before, .add-field-footer input[type="checkbox"]:checked::before { transform: scale(1); }
                .checkbox-input:disabled, .field-item input[type="checkbox"]:disabled, .add-field-footer input[type="checkbox"]:disabled { opacity: 0.6; cursor: not-allowed; border-color: var(--border-color); }
                .checkbox-input:disabled:checked { border-color: var(--green-accent); opacity: 0.6; }
                .checkbox-input:disabled::before { box-shadow: inset 1em 1em var(--light-text); }

                .interface-actions-compact { margin-top: 1rem; display: flex; flex-direction: column; gap: 0.5rem; }
                .field-selector-button { width: 100%; padding: 0.6rem 0.75rem; background-color: var(--border-color); border: none; color: var(--text-color); border-radius: 6px; cursor: pointer; font-size: 0.85rem; font-weight: 500; transition: all 0.2s; }
                .field-selector-button:hover { background-color: #5a6578; }
                .cancel-interface-button { width: 100%; padding: 0.6rem 0.75rem; background-color: rgba(239, 68, 68, 0.1); border: 1px solid var(--danger-color); color: var(--danger-color); border-radius: 6px; cursor: pointer; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; }
                .cancel-interface-button:hover { background-color: rgba(239, 68, 68, 0.2); }
                
                .delete-custom-interface-button { 
                    width: 100%; padding: 0.6rem 0.75rem; background-color: rgba(239, 68, 68, 0.1); 
                    border: 1px solid var(--danger-color); color: var(--danger-color); border-radius: 6px; 
                    cursor: pointer; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; 
                    display: flex; align-items: center; justify-content: center; gap: 0.5rem; 
                }
                .delete-custom-interface-button:hover { background-color: rgba(239, 68, 68, 0.2); }

                .cancellation-indicator { margin-top: auto; text-align: center; padding: 0.6rem 0.75rem; background-color: var(--border-color); color: var(--light-text); border-radius: 6px; font-size: 0.85rem; font-weight: 600; }

                .uncancel-interface-button { 
                    width: 100%; padding: 0.6rem 0.75rem; 
                    background-color: rgba(59, 130, 246, 0.1); 
                    border: 1px solid var(--blue-color); 
                    color: var(--blue-color); 
                    border-radius: 6px; cursor: pointer; 
                    font-size: 0.85rem; font-weight: 600; 
                    transition: all 0.2s; 
                    display: flex; align-items: center; justify-content: center; gap: 0.5rem;
                }
                .uncancel-interface-button:hover { background-color: rgba(59, 130, 246, 0.2); }

                .user-management-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; align-items: start; }
                h4 { font-size: 1rem; font-weight: 600; color: var(--light-text); margin: 0 0 1rem 0; }
                .assigned-users-list ul { list-style: none; padding: 0; margin: 0; max-height: 250px; overflow-y: auto; }
                .assigned-users-list li { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; margin-bottom: 0.5rem; }
                .assigned-users-list li button { background: none; border: none; color: var(--danger-color); font-size: 1.2rem; cursor: pointer; }
                .no-users-message { color: var(--light-text); font-style: italic; padding: 1rem; text-align: center; border-radius: 6px; border: 1px dashed var(--border-color); min-height: 52.5px; display:flex; align-items:center; justify-content:center; }
                .autocomplete-wrapper { position: relative; }
                .autocomplete-results { position: absolute; top: 100%; left: 0; right: 0; background-color: var(--card-bg); border: 1px solid var(--border-color); border-top: none; border-radius: 0 0 6px 6px; z-index: 100; max-height: 200px; overflow-y: auto; }
                .autocomplete-item { padding: 0.75rem 1rem; cursor: pointer; }
                .autocomplete-item:hover { background-color: #4a5568; }
                .autocomplete-item strong { color: var(--text-color); }
                .autocomplete-item span { color: var(--light-text); font-size: 0.9em; margin-left: 0.5rem; }

                .schedule-section-header {
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;
                }
                .schedule-section-header .form-section-title {
                    margin-bottom: 0; padding-bottom: 0; border-bottom: none;
                }

                .interface-actions-compact.hidden { opacity: 0; pointer-events: none; }
                .interface-actions-compact.visible { opacity: 1; pointer-events: auto; }

                .toast-notification {
                    position: fixed; top: 2rem; right: 2rem; background-color: var(--green-accent); color: var(--dark-bg);
                    padding: 1rem 1.5rem; border-radius: 8px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    display: flex; align-items: center; gap: 0.75rem; font-weight: 600; z-index: 9999;
                    pointer-events: none;
                    animation: slideInToast 0.3s cubic-bezier(0.16, 1, 0.3, 1), fadeOutToast 0.3s ease-in 2.7s forwards;
                }
                @keyframes slideInToast { from { transform: translateX(120%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
                @keyframes fadeOutToast { to { opacity: 0; visibility: hidden; } }

                .checkbox-item-wrapper { transition: border-color 0.3s ease, box-shadow 0.3s ease; }
                .checkbox-item-wrapper.is-selected { border-color: var(--green-accent); box-shadow: 0 4px 12px rgba(72, 187, 120, 0.15); animation: selectionPulse 0.4s ease-out; }
                @keyframes selectionPulse { 0% { transform: scale(1); } 50% { transform: scale(1.03); } 100% { transform: scale(1); } }

                .schedule-editor { display: grid; grid-template-columns: 280px 1fr; gap: 1.5rem; }
                .unassigned-pool { background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; }
                .interface-list { min-height: 200px; padding: 0.5rem; border-radius: 6px; border: 2px dashed var(--border-color); }
                .draggable-item { padding: 0.6rem 0.8rem; background-color: var(--card-bg); border: 1px solid var(--border-color); border-radius: 6px; margin-bottom: 0.5rem; cursor: grab; font-size: 0.9rem; }
                .draggable-item:active { cursor: grabbing; background-color: #4a5568; }
                .schedule-content-wrapper { display: flex; flex-direction: column; gap: 1rem; }
                .drop-tabs-container { display: flex; flex-wrap: wrap; gap: 0.5rem; border-bottom: 1px solid var(--border-color); padding-bottom: 1rem; }
                .drop-tab, .add-drop-tab-button { padding: 0.6rem 1.2rem; border: 1px solid var(--border-color); border-radius: 6px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
                .drop-tab { background-color: var(--input-bg); color: var(--light-text); }
                .drop-tab:hover { background-color: var(--card-bg); }
                .drop-tab.active { background-color: var(--green-accent); color: var(--dark-bg); border-color: var(--green-accent); }
                .add-drop-tab-button { background-color: transparent; color: var(--green-accent); border-style: dashed; }
                .add-drop-tab-button:hover { background-color: rgba(72, 187, 120, 0.1); border-color: var(--green-accent); }

                .active-drop-area { padding: 1rem; border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--input-bg); min-height: 300px; }
                .drop-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
                .drop-name-editor { display: flex; align-items: center; gap: 0.35rem; flex: 1; min-width: 0; margin-right: 1vw; }
                .drop-name-prefix { font-weight: 600; font-size: 1.1rem; color: var(--text-color); white-space: nowrap; }
                .drop-name-input { font-weight: 600; border: 1px solid transparent; border-radius: 4px; background-color: transparent; flex: 1; min-width: 0; padding: 0.25rem; font-size: 1.1rem; color: var(--text-color); transition: border-color 0.2s, background-color 0.2s; }
                .drop-name-input:hover { border-color: var(--border-color); }
                .drop-name-input:focus { outline: none; border-color: var(--green-accent); background-color: var(--input-bg); }
                .drop-header-actions { display: flex; align-items: center; gap: 1rem; }
                .remove-drop-button { background: none; border: none; font-size: 1.5rem; color: var(--light-text); cursor: pointer; }
                .remove-drop-button:hover { color: var(--danger-color); }
                .drop-placeholder { text-align: center; color: var(--light-text); padding: 2rem; }

                .unassigned-warning-message {
                    font-size: 0.85rem; color: var(--optional-override-color); background-color: var(--optional-override-bg); border: 1px solid var(--optional-override-color); padding: 0.75rem; border-radius: 6px; margin-bottom: 1rem; text-align: center; font-weight: 500;
                }

                .task-table-container { overflow-x: auto; }
                .task-table { width: 100%; border-collapse: collapse; }
                .task-table th, .task-table td { padding: 0.75rem; text-align: left; font-size: 0.9rem; vertical-align: middle; }
                .task-table thead { background-color: #374151; }
                .task-table th { font-weight: 600; color: var(--light-text); }

                .interface-group-header { cursor: pointer; user-select: none; }
                .interface-group-header td { background-color: #374151; font-weight: 600; color: var(--text-color); padding: 0; overflow: hidden; }
                .interface-header-cell { display: flex; justify-content: space-between; align-items: center; width: 100%; padding: 0.75rem 1rem; box-sizing: border-box; }
                .interface-header-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; flex-grow: 1; flex-shrink: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
                .toggle-icon { color: var(--light-text); transition: transform 0.2s ease-in-out; flex-shrink: 0; }
                
                .interface-header-actions { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; margin-left: 1rem; }

                .copy-dates-button {
                    background-color: var(--border-color); color: var(--text-color); border: none; border-radius: 4px;
                    padding: 0.3rem 0.6rem; font-size: 0.75rem; font-weight: 500; cursor: pointer;
                    transition: background-color 0.2s; flex-shrink: 0; white-space: nowrap;
                    display: inline-flex; align-items: center; gap: 0.3rem;
                }
                .copy-dates-button:hover { background-color: #5a6578; }
                .copy-dates-button.paste { margin-left: 0; background-color: var(--input-bg); border: 1px solid var(--border-color); }
                .copy-dates-button.paste:hover:not(:disabled) { background-color: var(--border-color); }
                .copy-dates-button:disabled { opacity: 0.5; cursor: not-allowed; }

                .unschedule-button {
                    background-color: transparent; color: var(--light-text); border: 1px solid var(--border-color); border-radius: 4px; padding: 0.3rem; font-size: 0.75rem; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; justify-content: center; line-height: 1;
                }
                .unschedule-button:hover {
                    background-color: var(--error-bg); color: var(--danger-color); border-color: var(--danger-color);
                }

                .task-row { border-left: 2px solid var(--border-color); }
                .task-row td { border-bottom: 1px solid var(--border-color); }
                .task-row:last-child td { border-bottom: none; }
                .task-name-cell { padding-left: 1.5rem; color: var(--light-text); }

                .task-table input[type="date"] { background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); padding: 0.6rem; color-scheme: dark; }
                .task-table input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.6) brightness(1.2); cursor: pointer; }

                .multi-select-container { position: relative; min-width: 150px; }
                .selected-users-pills { display: flex; align-items: center; flex-wrap: wrap; gap: 4px; border: 1px solid var(--border-color); border-radius: 6px; padding: 4px 0.75rem; min-height: 42px; cursor: pointer; background-color: var(--input-bg); transition: border-color 0.2s; }
                .selected-users-pills:hover { border-color: var(--light-text); }
                .user-pill { display: flex; justify-content: space-between; align-items: center; background-color: var(--border-color); border-radius: 4px; padding: 0.25rem 0.5rem; font-size: 0.8rem; }
                .user-pill button { background: none; border: none; margin-left: 0.5rem; font-size: 1rem; cursor: pointer; color: var(--light-text); }
                .no-users-text { color: var(--light-text); font-style: normal; font-size: 0.9rem; }
                .user-dropdown-list { position: absolute; background: var(--card-bg); border: 1px solid var(--border-color); border-radius: 6px; z-index: 10; width: 100%; max-height: 150px; overflow-y: auto; margin-top: 0.25rem; }
                .user-dropdown-item { padding: 0.5rem; cursor: pointer; font-size: 0.9rem; }
                .user-dropdown-item:hover { background-color: #4a5568; }
                .user-dropdown-item.disabled { color: var(--light-text); cursor: not-allowed; }

                .form-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem; }
                .action-button { padding: 0.75rem 1.5rem; border: none; border-radius: 6px; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .action-button.primary { background-color: var(--green-accent); color: var(--dark-bg); }
                .action-button.primary:hover:not(:disabled) { background-color: #38a169; }
                .action-button.secondary { background-color: var(--border-color); color: var(--text-color); }
                .action-button.secondary:hover { background-color: #5a6578; }
                .action-button.danger { background-color: var(--danger-color); color: var(--text-color); }
                .action-button.danger:hover:not(:disabled) { background-color: #dc2626; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }

                .form-feedback { text-align: center; padding: 0.75rem; border-radius: 6px; margin-bottom: 1.5rem; }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error.small { padding: 0.5rem; font-size: 0.9rem; }

                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; padding: 1rem; }
                .modal-content { background: var(--card-bg); border: 1px solid var(--border-color); padding: 0; border-radius: 8px; width: 90%; max-width: 500px; max-height: 90vh; display: flex; flex-direction: column; }
                .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid var(--border-color); }
                .modal-header h3 { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .modal-close-button { background: transparent; border: none; font-size: 1.5rem; cursor: pointer; color: var(--light-text); }
                .modal-body { overflow-y: auto; overflow-x: hidden; padding: 0; }
                .modal-footer { display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1rem 1.5rem; border-top: 1px solid var(--border-color); }
                .modal-content.large-modal { max-width: 800px; }

                .category-content::-webkit-scrollbar, .modal-body::-webkit-scrollbar, .user-dropdown-list::-webkit-scrollbar { width: 14px; }
                .category-content::-webkit-scrollbar-track, .modal-body::-webkit-scrollbar-track, .user-dropdown-list::-webkit-scrollbar-track { background: var(--input-bg); }
                .category-content::-webkit-scrollbar-thumb, .modal-body::-webkit-scrollbar-thumb, .user-dropdown-list::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 10px; border: 3px solid var(--input-bg); }
                .category-content::-webkit-scrollbar-thumb:hover, .modal-body::-webkit-scrollbar-thumb:hover, .user-dropdown-list::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                .dropdown-actions { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--border-color); background-color: var(--dark-bg); position: sticky; top: 0; z-index: 1; }
                .dropdown-actions span { font-size: 0.85rem; color: var(--light-text); font-weight: 500; }
                .dropdown-actions button { background: none; border: none; color: var(--green-accent); font-weight: 600; cursor: pointer; padding: 0.25rem 0.5rem; }
                .dropdown-actions .add-field-button { background-color: var(--border-color); color: var(--text-color); border-radius: 4px; margin-left: 0.5rem; }
                
                .import-csv-button {
                    display: inline-flex;
                    align-items: center;
                    background-color: transparent;
                    color: var(--blue-color) !important;
                    border: 1px solid var(--blue-color) !important;
                    border-radius: 4px;
                    margin-left: 0.5rem;
                    padding: 0.25rem 0.5rem !important;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }
                .import-csv-button:hover:not(:disabled) {
                    background-color: var(--blue-bg) !important;
                }
                .import-csv-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .spinner-small {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(147, 197, 253, 0.3);
                    border-top-color: var(--blue-color);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 5px;
                }
                
                .field-groups-container { display: flex; flex-direction: column; }
                .file-group-header {
                    font-size: 0.9rem; font-weight: 700; color: var(--green-accent); background-color: var(--dark-bg); padding: 0.5rem 1.5rem; border-bottom: 1px solid var(--border-color); border-top: 1px solid var(--border-color); margin: 0; text-transform: uppercase; letter-spacing: 0.05em;
                }
                .file-group:first-child .file-group-header { border-top: none; }
                .field-list { list-style: none; padding: 0.5rem; margin: 0; }

                .field-item { display: flex; align-items: flex-start; padding: 0.75rem 1rem; border-radius: 4px; transition: background-color 0.2s; }
                .field-item:hover { background-color: var(--dark-bg); }
                .field-item input[type="checkbox"] { margin-right: 1rem; margin-top: 2px; }
                .field-item input[type="checkbox"] {
                    -webkit-appearance: none; appearance: none;
                    flex-shrink: 0; width: 18px; height: 18px; border: 2px solid var(--border-color); border-radius: 4px;
                    background-color: var(--input-bg); display: flex; align-items: center; justify-content: center;
                    cursor: pointer; transition: border-color 0.2s;
                }
                .field-item-content { flex-grow: 1; display: flex; flex-direction: column; }
                .field-item label.field-item-label-container { cursor: pointer; display: flex; align-items: center; justify-content: space-between; width: 100%; color: var(--text-color); margin-bottom: 0.25rem; }

                .field-label-text { flex-grow: 1; }
                .field-tags-and-actions { display: flex; align-items: center; gap: 0.75rem; }
                .field-action-btn { background: none; border: none; cursor: pointer; padding: 0.25rem; font-size: 0.8rem; }
                .field-action-btn.edit { color: var(--light-text); }
                .field-action-btn.edit:hover { color: #60a5fa; }
                .field-action-btn.delete { color: var(--light-text); }
                .field-action-btn.delete:hover { color: var(--danger-color); }

                .status-tag { display: inline-flex; align-items: center; padding: 0.1rem 0.4rem; border-radius: 9px; font-size: 0.7rem; font-weight: 700; text-transform: capitalize; white-space: nowrap; margin-right: 0.5rem; position: relative; }
                .status-tag.mandatory { color: var(--error-color); background-color: var(--error-bg); }
                .status-tag.optional { color: var(--light-text); background-color: var(--border-color); }
                .status-tag.overridden-tag { color: var(--optional-override-color); background-color: var(--optional-override-bg); border: 1px dashed var(--optional-override-color); cursor: pointer; }

                .override-button { background: none; border: none; color: inherit; font-size: 0.7em; padding: 0 0 0 4px; cursor: pointer; opacity: 0; transition: opacity 0.2s; line-height: 1; vertical-align: middle; }
                .status-tag.mandatory.interactive-tag:hover .override-button { opacity: 0.7; }
                .override-button:hover { opacity: 1 !important; color: var(--error-color); }

                .omission-reason-display { display: flex; align-items: flex-start; gap: 0.5rem; padding: 0.5rem; margin-top: 0.25rem; background-color: var(--dark-bg); border-radius: 4px; border: 1px solid var(--border-color); }
                .reason-icon { color: #fcd34d; flex-shrink: 0; margin-top: 3px; }
                .reason-text { margin: 0; font-size: 0.85rem; color: var(--light-text); white-space: pre-wrap; word-break: break-word; }

                /* --- UPDATED: Interface Level Warning Icon --- */
                .card-deadline-warning {
                    position: absolute; 
                    
                    /* Back to your favorite top-right position! */
                    top: -0.65rem; 
                    right: -0.65rem;
                    
                    font-size: 1.25rem; 
                    color: #f59e0b; 
                    background-color: var(--card-bg); 
                    border-radius: 50%;
                    padding: 0.1rem;
                    
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    
                    /* FIX 1: Lowered to z-index 2 so it safely slides UNDER the sticky header when scrolling! */
                    z-index: 2; 
                    cursor: help;
                }

                .no-fields-message-container {
                    padding: 4rem 2rem;
                    text-align: center;
                    color: var(--light-text);
                    background-color: var(--input-bg);
                    border: 2px dashed var(--border-color);
                    border-radius: 8px;
                    margin: 1.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                }
                .no-fields-message-container p {
                    margin: 0;
                    font-size: 1.05rem;
                }
                .no-fields-message-container .sub-text {
                    font-size: 0.85rem;
                    opacity: 0.8;
                }

                .custom-tag { font-size: 0.7rem; font-weight: 700; padding: 0.1rem 0.4rem; border-radius: 9px; color: #93c5fd; background-color: rgba(59, 130, 246, 0.2); }
                .modal-content.large-modal { max-width: 800px; }

                .add-field-form { padding: 1.5rem; margin: 0 1rem 1rem 1rem; background-color: var(--dark-bg); border: 1px solid var(--border-color); border-radius: 8px; }
                .add-field-form h4 { margin: 0 0 1rem 0; font-weight: 600; }
                .add-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
                .add-field-grid textarea { grid-column: 1 / -1; }
                .add-field-grid .span-two-columns { grid-column: 1 / -1; }

                .add-field-grid select {
                    width: 100%; padding: 0.75rem 1rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; font-size: 1rem; color: var(--text-color); transition: border-color 0.2s; box-sizing: border-box; color-scheme: dark; -webkit-appearance: none; appearance: none;
                    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="%23a0aec0" viewBox="0 0 16 16"><path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 1em; cursor: pointer;
                }
                .add-field-grid select:focus { outline: none; border-color: var(--green-accent); }

                .add-field-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 1rem; }
                .field-form-checkboxes { display: flex; gap: 1rem; }
                .add-field-footer label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.9rem; }
                .add-field-footer input[type="checkbox"] { margin-right: 0.5rem; }

                .modal-content.cancel-modal { max-width: 450px; }
                .modal-content.cancel-modal .modal-body { text-align: center; padding: 2rem; }
                .cancel-modal-icon-wrapper { width: 3.5rem; height: 3.5rem; background-color: var(--error-bg); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; }
                .cancel-modal-icon { color: var(--danger-color); font-size: 1.75rem; }
                .cancel-modal-title { font-size: 1.25rem; font-weight: 600; color: var(--text-color); margin-bottom: 0.75rem; }
                .cancel-modal-text { color: var(--light-text); font-size: 0.95rem; margin-bottom: 1.5rem; }
                .cancel-modal .input-label { text-align: left; width: 100%; }

                .danger-zone { border: 1px solid #b91c1c; border-radius: 8px; margin-top: 2.5rem; padding: 1.5rem; background-color: rgba(239, 68, 68, 0.05); }
                .danger-zone-title { font-size: 1.1rem; font-weight: 600; color: #fca5a5; display: flex; align-items: center; gap: 0.5rem; margin: 0 0 1.5rem 0; padding-bottom: 0.75rem; border-bottom: 1px solid rgba(239, 68, 68, 0.3); }
                .danger-zone-actions { display: flex; flex-direction: column; gap: 1.5rem; }
                .danger-action { display: flex; justify-content: space-between; align-items: center; gap: 1rem; }
                .danger-action p { margin: 0; color: var(--light-text); font-size: 0.9rem; line-height: 1.4; }
                .danger-action p strong { color: var(--text-color); }
                .delete-button { background-color: var(--danger-color); color: white; }
                .delete-button:hover:not(:disabled) { background-color: #dc2626; }
                .delete-button:disabled { background-color: #9ca3af; opacity: 0.7; cursor: not-allowed; }

                .modal-content.auth-modal { max-width: 450px; }
                .modal-content.auth-modal .modal-body { padding: 1.5rem; }
                .auth-modal .input-group { margin-bottom: 0.5rem; }
                .auth-modal .input-label { margin-bottom: 0.25rem; }
                .password-input-container { position: relative; width: 100%; }

                .auth-modal input[type="password"], .auth-modal input[type="text"] {
                     width: 100%; padding: 0.75rem 2.5rem 0.75rem 1rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; font-size: 1rem; color: var(--text-color); transition: border-color 0.2s; box-sizing: border-box;
                }
                .auth-modal input[type="password"]:focus, .auth-modal input[type="text"]:focus { outline: none; border-color: var(--green-accent); }

                .password-toggle-icon { position: absolute; top: 50%; right: 0.75rem; transform: translateY(-50%); background: none; border: none; color: #cbd5e0; cursor: pointer; padding: 0.25rem; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
                .password-toggle-icon:hover { color: #e2e8f0; }
                .auth-modal input[type="password"]::-ms-reveal { display: none; }
                .password-input-container input:-webkit-autofill ~ .password-toggle-icon, .password-input-container input:autofill ~ .password-toggle-icon { color: var(--dark-bg); }
                .password-input-container input:-webkit-autofill ~ .password-toggle-icon:hover, .password-input-container input:autofill ~ .password-toggle-icon:hover { color: var(--card-bg); }

                .loading-state { text-align: center; color: var(--text-color); padding-top: 5rem; }
                .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .button-spinner { animation: spin 1s linear infinite; }
                
                /* --- Custom Interface Card --- */
                .custom-interface-card {
                    border: 2px dashed var(--border-color);
                    border-radius: 8px;
                    background-color: transparent;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 1.5rem;
                    min-height: 100px;
                }
                .custom-interface-card:hover {
                    border-color: var(--green-accent);
                    background-color: rgba(72, 187, 120, 0.05);
                }
                .custom-interface-card-content {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                    color: var(--light-text);
                    font-weight: 500;
                    text-align: center;
                }
                .custom-interface-card:hover .custom-interface-card-content {
                    color: var(--green-accent);
                }
                .custom-interface-card .plus-symbol {
                    font-size: 1.5rem;
                    line-height: 1;
                }
            `}</style>
        </div>
    );
}